import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Breadcrumb,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
  Stack,
  Modal,
  InputGroup,
  FormSelect,
} from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
// import "react-bootstrap-typeahead/css/Typeahead.css";
import PubSub from "pubsub-js";
import moment from "moment";
import { ROUTES } from "../../constants/webRouting";
import solisPowerApi from "../../api/solisPowerApi";
import CityState from "../../constants/CityState.json";
import * as constants from "../../constants/CONSTANT";
import authApi from "../../api/authApi";
import { regExPattern } from "../../utils/regExPattern";
import {
  formatDate,
  formatDateForCreatedDate,
  getUserDetails,
} from "../../utils/helper";
// import jwt_decode from "jwt-decode";
import { validationMessages } from "../../utils/messages";
import { handleUppercaseChange } from "../common/UpperCaseMethod";
import PERMISSIONS from "../../constants/permissions";
import { FaCopy, FaTrash } from "react-icons/fa";
import { isMobile, BrowserView, MobileView } from "react-device-detect";
import { titles } from "../../constants/CONSTANT";

const LeadEdit = (props) => {
  const userInfo = getUserDetails();

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  // Modified By Farhan Khan on 16-Feb-2026 (Fetch lead by ID when edit URL has id but state is empty - ensures Lead Source and other fields pre-fill correctly)
  const editLeadIdFromUrl = params?.id || (location?.pathname?.match(/\/leads\/([^/]+)\/e/) || [])[1];

  // console.log("userInfo", userInfo);
  const initialValues = {
    owner_ids: [{ value: userInfo?.id, label: userInfo?.username }],
    country: "India",
    // connection_modification :"No modification",
    // lead_status: "Enquiry",
    is_gst: "Yes",
    //city: "Jaipur",
  };

  // Check if user is SUPER_ADMIN (defined early for use in multiple places)
  const isSuperAdmin = userInfo?.userrole === "SUPER_ADMIN";

  // Define initial errors i.e. required field and their message.
  const initialErrors = {};

  // Tele Caller Information fields - only required for Tele-Caller and SUPER_ADMIN
  if (isSuperAdmin || userInfo?.user_type === "Tele-Caller") {
    initialErrors.lead_name = { message: validationMessages.leadName.required };
    initialErrors.phone = { message: validationMessages.phone.required };
  }

  // Project Details fields - only required for Sales Person and SUPER_ADMIN
  if (isSuperAdmin || userInfo?.user_type === "Sales Person") {
    initialErrors.on_site_contact_person_no = {
      message: validationMessages.contact_no.required,
    };
    initialErrors.leadtype = { message: validationMessages.leadType.required };
    // initialErrors.lead_status = {
    //   message: validationMessages.leadStatus.required,
    // };
  }

  // Common fields - required for all users
  initialErrors.owner_ids = { message: validationMessages.ownerIds.required };
  // City is optional - removed from required fields

  // Debug: Log initialErrors for Sales Person
  if (userInfo?.user_type === "Sales Person" && !isSuperAdmin) {
  }

  // lead state - handle prefilledPhone from New Lead pre-check modal
  const [lead, setLead] = useState(() => {
    const s = location.state;
    if (s?.prefilledPhone && !s?.id) {
      return { ...initialValues, phone: String(s.prefilledPhone).replace(/\D/g, "").slice(0, 10) };
    }
    return s && (s.id || s.lead_name) ? s : initialValues;
  });
  //errors state
  const [errors, setErrors] = useState(!location.state ? initialErrors : {});

  // Modified By Farhan Khan on 16-Feb-2026 (Fetch lead by ID when navigating to edit page without state - ensures Lead Source pre-fills on edit)
  useEffect(() => {
    if (!editLeadIdFromUrl) return;
    const hasValidLead = lead?.id && (lead?.lead_name || lead?.leadsource !== undefined);
    if (hasValidLead) return;
    let isMounted = true;
    solisPowerApi.fetchLeadById(editLeadIdFromUrl).then((fetched) => {
      if (!isMounted || !fetched) return;
      setLead(fetched);
      setErrors({});
    });
    return () => { isMounted = false; };
  }, [editLeadIdFromUrl]);
  // End Here Comment By Farhan Khan on 16-Feb-2026

  // Amount state - separate from lead state (for Account creation)
  const [accountAmount, setAccountAmount] = useState("");

  // Project Information & Address rows (Add Row)
  const [projectLineItems, setProjectLineItems] = useState([]);
  // Validation errors for Add Row (per-row, keyed by row index)
  const [projectRowValidationErrors, setProjectRowValidationErrors] = useState({});

  // Helper: check if row has any filled value - do not save completely empty rows
  const isRowFilled = (row) => {
    if (!row || typeof row !== "object") return false;
    const fields = [
      "site_name", "leadtype", "project_status", "hold_reason", "delay_reason",
      "type_of_project", "on_site_contact_person_no", "plant_capacity_required_by_client",
      "type_of_roof", "type_of_connection", "sanction_load", "connection_modification",
      "connection_modification_applicable", "no_of_floors", "remark", "average_monthly_bill",
      "dcr", "description", "location_link", "address", "state", "city", "pincode", "country",
    ];
    return fields.some((f) => {
      const v = row[f];
      return v != null && String(v).trim() !== "";
    });
  };

  // Helper: one empty project row - ensure at least one row visible by default
  const getEmptyProjectRow = () => ({
    site_name: "",
    leadtype: "",
    // lead_status: "",
    project_status: "Enquiry",
    hold_reason: "",
    delay_reason: "",
    type_of_project: "",
    on_site_contact_person_no: "",
    plant_capacity_required_by_client: "",
    type_of_roof: "",
    type_of_connection: "",
    sanction_load: "",
    connection_modification: "",
    connection_modification_applicable: "",
    no_of_floors: "",
    remark: "",
    average_monthly_bill: "",
    dcr: "",
    description: "",
    location_link: "",
    address: "",
    state: "",
    city: "",
    pincode: "",
    country: lead?.country || "India",
  });

  // Sync project line items when lead is loaded (from detail view state or fetch by id)
  useEffect(() => {
    if (!lead?.id) {
      // Create mode: no default row - user adds via "Add Project" button only
      setProjectLineItems((prev) => (prev?.length > 0 ? prev : []));
      return;
    }
    if (Array.isArray(lead?.project_line_items) && lead.project_line_items.length > 0) {
      const hasSiteVisitDate = lead?.customer_site_visit_date && String(lead.customer_site_visit_date).trim() !== "";
      setProjectLineItems(lead.project_line_items.map((row) => {
        const norm = (v) => (v != null && String(v).trim() !== "" ? sanitizeNumericInput(v) : (v ?? ""));
        const base = { ...row, plant_capacity_required_by_client: norm(row.plant_capacity_required_by_client), sanction_load: norm(row.sanction_load) };
        if (hasSiteVisitDate) {
          base.project_status = "Site Visit Scheduled";
        }
        return base;
      }));
      return;
    }
    if (lead.project_line_items && lead.project_line_items.length === 0) {
      setProjectLineItems([]);
      return;
    }
    // Edit mode: lead may come from list without project_line_items — fetch them
    solisPowerApi.fetchLeadProjectLineItems(lead.id).then((rows) => {
      const items = Array.isArray(rows) ? rows : [];
      const hasSiteVisitDate = lead?.customer_site_visit_date && String(lead.customer_site_visit_date).trim() !== "";
      const normalized = items.length > 0 ? items.map((row) => {
        const norm = (v) => (v != null && String(v).trim() !== "" ? sanitizeNumericInput(v) : (v ?? ""));
        const base = { ...row, plant_capacity_required_by_client: norm(row.plant_capacity_required_by_client), sanction_load: norm(row.sanction_load) };
        if (hasSiteVisitDate) {
          base.project_status = "Site Visit Scheduled";
        }
        return base;
      }) : [];
      setProjectLineItems(normalized);
    });
  }, [lead?.id]);

  // Sanitize input for single digit only (0-9), trim paste to first digit
  const sanitizeSingleDigitInput = (val) => {
    const digits = String(val || "").replace(/\D/g, "");
    return digits.length ? digits[0] : "";
  };

  // Sanitize numeric input: digits only, remove leading zeros (e.g. "01" -> "1", "007" -> "7")
  const sanitizeNumericInput = (val) => {
    const digits = String(val || "").replace(/\D/g, "");
    if (digits === "") return "";
    const num = parseInt(digits, 10);
    return isNaN(num) ? "" : String(num);
  };

  // Validate single digit format (when non-empty)
  const isValidSingleDigit = (val) => {
    const s = String(val || "").trim();
    if (s === "") return true;
    return /^[0-9]$/.test(s);
  };

  // Validate numeric integer (any valid int: 1, 2, 14, 100, etc.)
  const isValidNumericInteger = (val) => {
    const s = String(val || "").trim();
    if (s === "") return true;
    return /^\d+$/.test(s);
  };

  // Validate 10-digit contact number: trim, convert to string, exact length 10, digits only
  const isValidOnSiteContactNo = (val) => {
    if (val == null) return false;
    const str = String(val).trim();
    if (str === "") return false;
    const digits = str.replace(/\D/g, "");
    return digits.length === 10 && /^[0-9]{10}$/.test(digits);
  };

  // Validate current project/address row before allowing Add Row (required fields only)
  const validateProjectRowForAddRow = (row) => {
    const err = {};
    if (!row || typeof row !== "object") return err;
    // Modified By Farhan Khan on 16-Feb-2026 (Site Name is required in Project Section)
    if (!row.site_name || String(row.site_name).trim() === "") {
      err.site_name = validationMessages.siteName?.required || "Please provide site name";
    }
    if (!row.leadtype || String(row.leadtype).trim() === "") {
      err.leadtype = validationMessages.leadType?.required || "Please provide lead type";
    }
    // if (!row.lead_status || String(row.lead_status).trim() === "") {
    //   err.lead_status = validationMessages.leadStatus?.required || "Please provide lead status";
    // }
    const contactNo = row.on_site_contact_person_no;
    if (contactNo == null || String(contactNo).trim() === "") {
      err.on_site_contact_person_no = validationMessages.contact_no?.required || "Please provide contact number";
    } else if (!isValidOnSiteContactNo(contactNo)) {
      err.on_site_contact_person_no = "On-site Contact Person No must be exactly 10 digits";
    }
    if (!row.address || String(row.address).trim() === "") {
      err.address = "Please provide address";
    }
    if (!row.state || String(row.state).trim() === "") {
      err.state = "Please provide state";
    }
    if (!row.city || String(row.city).trim() === "") {
      err.city = validationMessages.city?.required || "Please provide city";
    }
    if (!row.pincode || String(row.pincode).trim() === "") {
      err.pincode = validationMessages.pinCode?.required || "Please provide pincode";
    }
    if (row.sanction_load != null && String(row.sanction_load).trim() !== "" && !isValidNumericInteger(row.sanction_load)) {
      err.sanction_load = "Only numeric value is allowed";
    }
    if (row.plant_capacity_required_by_client != null && String(row.plant_capacity_required_by_client).trim() !== "" && !isValidNumericInteger(row.plant_capacity_required_by_client)) {
      err.plant_capacity_required_by_client = "Only numeric value is allowed";
    }
    return err;
  };

  // Clear Project Details errors for Tele-Caller on mount (not for SUPER_ADMIN)
  useEffect(() => {
    if (userInfo?.user_type === "Tele-Caller" && !isSuperAdmin) {
      const projectDetailFields = [
        "leadtype",
        "lead_status",
        "on_site_contact_person_no",
        "no_of_floors",
        "type_of_roof",
        "dcr",
        "plant_capacity_required_by_client",
        "type_of_connection",
        "sanction_load",
        "average_monthly_bill",
        "type_of_project",
      ];
      setErrors((prev) => {
        const newErrors = { ...prev };
        projectDetailFields.forEach((field) => {
          delete newErrors[field];
        });
        return newErrors;
      });
    }
  }, [userInfo?.user_type, isSuperAdmin]);

  // Clear Tele Caller Information errors for Sales Person on mount (not for SUPER_ADMIN)
  // This runs immediately to clear any Tele Caller fields that shouldn't be required for Sales Person
  useEffect(() => {
    if (userInfo?.user_type === "Sales Person" && !isSuperAdmin) {
      const teleCallerInfoFields = [
        "lead_name",
        "phone",
      ];
      // Forcefully clear these errors immediately
      setErrors((prev) => {
        const newErrors = { ...prev };
        teleCallerInfoFields.forEach((field) => {
          delete newErrors[field];
        });
        return newErrors;
      });
    }
  }, [userInfo?.user_type, isSuperAdmin]);

  useEffect(() => {
    console.log("errors", errors);
  }, [errors]);

  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);

  // Handle City & state values
  // const [states, setStates] = useState([
  //   ...new Set(CityState.map((item) => item.state)),
  // ]);

  const states = [...new Set(CityState.map((item) => item.state))];
  const [cities, setCities] = useState(null);
  const [customerCities, setCustomerCities] = useState(null);

  // If state value changed set the cities according to state
  useEffect(() => {
    setCities([
      ...new Set(
        CityState.filter((item) => item.state == lead?.state).map(
          (item) => item.name
        )
      ),
    ]);
  }, [lead?.state]);

  // If customer_state value changed set the customer_cities according to customer_state
  useEffect(() => {
    setCustomerCities([
      ...new Set(
        CityState.filter((item) => item.state == lead?.customer_state).map(
          (item) => item.name
        )
      ),
    ]);
  }, [lead?.customer_state]);

  const [statusArray, setStatusArray] = useState(constants.LEAD_STATUS_VALUES);
  const [statusProjectArray, setStatusProjectArray] = useState(constants.PROJECT_STATUS_VALUES);

  const [products, setProducts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [refContact, setRefContact] = useState([]);
  // Modified By Farhan Khan on 16-Feb-2026 (Fixed dropdown prefill mapping issue on edit - initialize from constants so Lead Source never shows empty)
  const [leadSourceOptions, setLeadSourceOptions] = useState(() =>
    Array.isArray(constants?.sourceNames) ? constants.sourceNames : []
  );
  // End Here Comment By Farhan Khan on 16-Feb-2026
  // const [offers, setOffers] = useState([]);
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // Section completion states
  const [sectionCompletion, setSectionCompletion] = useState({
    leadInformation: false,
    serviceInformation: false,
    projectDetails: false,
    addressInformation: false,
  });

  // Active section state for blink effect
  const [activeSection, setActiveSection] = useState(null);

  // Section visibility state based on user type
  // SUPER_ADMIN can see and edit all sections
  const [visibleSections, setVisibleSections] = useState({
    teleCallerInfo: isSuperAdmin || userInfo?.user_type === "Tele-Caller",
    projectDetails: isSuperAdmin || userInfo?.user_type === "Sales Person",
    addressInfo: true, // Always visible
  });

  // Debug: Track visibleSections changes (moved after visibleSections declaration)
  useEffect(() => {
    console.log("visibleSections state changed:", visibleSections);
  }, [visibleSections]);

  async function init() {
    //   if ([PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_USER].some(permission => {
    //   return userInfo?.permissions.map(p => p.name).includes(permission);
    // })) {

    //   const resultedUsers = await solisPowerApi.fetchUsers();

    //   if (resultedUsers && Array.isArray(resultedUsers)) {

    //     setAssignUsers(resultedUsers);

    //     // Logged-in user ko dhundho
    //     const loggedInUser = resultedUsers.find(u => u.id === userInfo.id);

    //     // Sirf Tele-Caller users filter kro
    //     const teleCallers = resultedUsers.filter(u => u.user_type === "Tele-Caller");

    //     let autoSelectedOwners = [];

    //     // Logged-in user ko push karo (hamesha)
    //     if (loggedInUser) {
    //       autoSelectedOwners.push({
    //         value: loggedInUser.id,
    //         label: loggedInUser.username
    //       });
    //     }

    //     // Random Tele-Caller add kro
    //     if (teleCallers.length > 0) {
    //       const randomTeleCaller = teleCallers[Math.floor(Math.random() * teleCallers.length)];

    //       autoSelectedOwners.push({
    //         value: randomTeleCaller.id,
    //         label: randomTeleCaller.username
    //       });
    //     }

    //     // Ab default value set karo
    //     handleChange({
    //       target: {
    //         name: "owner_ids",
    //         value: autoSelectedOwners
    //       }
    //     });
    //   }
    // }

    const resultedUsers = await solisPowerApi.fetchUsersDropdown();

    if (resultedUsers && Array.isArray(resultedUsers)) {
      setAssignUsers(resultedUsers);

      // ---- CHECK EDIT OR CREATE ---- //
      const isEdit = !!lead?.id;
      const hasExistingOwners =
        Array.isArray(lead?.owner_ids) && lead.owner_ids.length > 0;

      // ---- IF EDIT CASE → DO NOTHING (existing values remain) ---- //
      if (isEdit && hasExistingOwners) {
        return; // STOP HERE
      }

      // ---- CREATE CASE → AUTO SELECT ---- //
      const defaultOwners = getDefaultOwners(resultedUsers, userInfo);

      handleChange({
        target: {
          name: "owner_ids",
          value: defaultOwners,
        },
      });
    }

    if (
      [PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_USER].some((permission) =>
        userInfo?.permissions.map((p) => p.name).includes(permission)
      )
    ) {

      // const resultedUsers = await solisPowerApi.fetchUsers();

      // if (resultedUsers && Array.isArray(resultedUsers)) {
      //   setAssignUsers(resultedUsers);

      //   // ---- CHECK EDIT OR CREATE ---- //
      //   const isEdit = !!lead?.id;
      //   const hasExistingOwners =
      //     Array.isArray(lead?.owner_ids) && lead.owner_ids.length > 0;

      //   // ---- IF EDIT CASE → DO NOTHING (existing values remain) ---- //
      //   if (isEdit && hasExistingOwners) {
      //     return; // STOP HERE
      //   }

      //   // ---- CREATE CASE → AUTO SELECT ---- //
      //   const defaultOwners = getDefaultOwners(resultedUsers, userInfo);

      //   handleChange({
      //     target: {
      //       name: "owner_ids",
      //       value: defaultOwners,
      //     },
      //   });
      // }
    }

    if (
      [PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_PRODUCT].some((permission) => {
        return userInfo?.permissions.map((p) => p.name).includes(permission);
      })
    ) {
      const fetchedContacts = await solisPowerApi.fetchProductsForEnquiry();
      Array.isArray(fetchedContacts) &&
        setProducts(
          fetchedContacts?.map((fetchedContact) => ({
            id: fetchedContact?.id,
            name: `${fetchedContact?.product_name || ""}`,
          }))
        );
    }

    // Lead Source: use constant source names (not DB-fetched types)
    if (
      [PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_CONTACT].some((permission) => {
        return userInfo?.permissions.map((p) => p.name).includes(permission);
      })
    ) {
      setLeadSourceOptions(Array.isArray(constants.sourceNames) ? constants.sourceNames : []);
    }

    if (
      [PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_MAKE].some((permission) => {
        return userInfo?.permissions.map((p) => p.name).includes(permission);
      })
    ) {
      const resultedAccounts = await solisPowerApi.fetchAccounts();
      resultedAccounts &&
        Array.isArray(resultedAccounts) &&
        setAccounts(resultedAccounts);

      // In Edit mode, find account by contact_id and populate accountAmount
      if (lead?.id && lead?.contact_id) {
        const relatedAccount = resultedAccounts?.find(
          (account) => account.contact_id === lead.contact_id
        );
        if (relatedAccount && relatedAccount.amount) {
          setAccountAmount(relatedAccount.amount.toString());
        }
      }
    }
  }

  useEffect(() => {
    init();
  }, []);

  // Modified By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering - load ALL active contacts on mount)
  useEffect(() => {
    fetchAllContactsForReference();
  }, []);
  // End Here Comment By Farhan Khan on 16-Feb-2026

  // Fetch account amount in Edit mode when lead has contact_id
  useEffect(() => {
    const fetchAccountAmount = async () => {
      // Only in Edit mode and if contact_id exists
      if (lead?.id && lead?.contact_id) {
        try {
          const resultedAccounts = await solisPowerApi.fetchAccounts();
          if (resultedAccounts && Array.isArray(resultedAccounts)) {
            // Find account with matching contact_id
            const relatedAccount = resultedAccounts.find(
              (account) => account.contact_id === lead.contact_id
            );
            if (relatedAccount && relatedAccount.amount) {
              setAccountAmount(relatedAccount.amount.toString());
            }
          }
        } catch (error) {
          console.error("Error fetching account amount:", error);
        }
      }
    };

    fetchAccountAmount();
  }, [lead?.id, lead?.contact_id]);

  async function fetchContacts() {
    if (
      [PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_CONTACT].some((permission) => {
        return userInfo?.permissions.map((p) => p.name).includes(permission);
      })
    ) {
      const fetchedRefrealContact =
        await solisPowerApi.fetchContactsForEnquiry();
      Array.isArray(fetchedRefrealContact) &&
        setRefContact(
          fetchedRefrealContact?.map((fetchRefContact) => ({
            id: fetchRefContact?.id,
            contact_name: `${fetchRefContact?.firstname || ""} ${fetchRefContact?.lastname || ""
              }`,
            title: fetchRefContact?.title,
          }))
        );
    }
  }

  // Modified By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering - fetch ALL active contacts)
  async function fetchAllContactsForReference() {
    if (
      ![PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_CONTACT].some((permission) => {
        return userInfo?.permissions.map((p) => p.name).includes(permission);
      })
    ) {
      return;
    }
    const contacts = await solisPowerApi.fetchContacts();
    setRefContact(
      Array.isArray(contacts)
        ? contacts.map((c) => ({
          id: c?.id,
          firstname: c?.firstname,
          contact_name: c?.contact_name || `${c?.firstname || ""} ${c?.lastname || ""}`.trim(),
          title: c?.title,
        }))
        : []
    );
  }
  // End Here Comment By Farhan Khan on 16-Feb-2026

  //   function getDefaultOwners(resultedUsers, userInfo) {
  //   const loggedInUser = resultedUsers.find(u => u.id === userInfo.id);
  //   const teleCallers = resultedUsers.filter(u => u.user_type === "Tele-Caller");

  //   let selected = [];

  //   if (loggedInUser) {
  //     selected.push({
  //       value: loggedInUser.id,
  //       label: loggedInUser.username
  //     });
  //   }

  //   if (teleCallers.length > 0) {
  //     const randomTele = teleCallers[Math.floor(Math.random() * teleCallers.length)];
  //     selected.push({
  //       value: randomTele.id,
  //       label: randomTele.username
  //     });
  //   }

  //   return selected;
  // }

  function getDefaultOwners(resultedUsers, userInfo) {
    const loggedInUser = resultedUsers.find((u) => u.id === userInfo.id);
    const teleCallers = resultedUsers.filter(
      (u) => u.user_type === "Tele-Caller" && u.id !== userInfo.id // exclude logged-in user
    );

    let selected = [];

    // Add logged-in user
    if (loggedInUser) {
      selected.push({
        value: loggedInUser.id,
        label: loggedInUser.username,
      });
    }

    // Add a tele-caller only if different from logged-in user
    if (teleCallers.length > 0) {
      const randomTele =
        teleCallers[Math.floor(Math.random() * teleCallers.length)];
      selected.push({
        value: randomTele.id,
        label: randomTele.username,
      });
    }

    return selected;
  }

  // Check section completion
  const checkSectionCompletion = useCallback(() => {
    // Lead Information section
    const leadInfoComplete =
      lead?.lead_name &&
      lead?.lead_name !== "" &&
      lead?.phone &&
      lead?.phone !== "" &&
      lead?.owner_ids?.length > 0;

    // Service Information section
    const hasAccounts =
      lead?.accounts?.filter((item) => item.value)?.length > 0;
    const serviceInfoComplete =
      lead?.product_id &&
      lead?.qty !== "" &&
      lead?.qty !== null &&
      lead?.qty !== undefined &&
      hasAccounts;

    // Project Details section - check important fields
    const projectDetailsComplete =
      lead?.leadtype &&
      lead?.leadtype !== "" &&
      // lead?.lead_status &&
      // lead?.lead_status !== "" &&
      lead?.on_site_contact_person_no &&
      lead?.on_site_contact_person_no !== "" &&
      lead?.no_of_floors &&
      lead?.no_of_floors !== "" &&
      lead?.type_of_roof &&
      lead?.type_of_roof !== "" &&
      lead?.dcr &&
      lead?.dcr !== "" &&
      lead?.plant_capacity_required_by_client &&
      lead?.plant_capacity_required_by_client !== "" &&
      lead?.type_of_connection &&
      lead?.type_of_connection !== "" &&
      lead?.sanction_load &&
      lead?.sanction_load !== "" &&
      lead?.average_monthly_bill &&
      lead?.average_monthly_bill !== "" &&
      lead?.type_of_project &&
      lead?.type_of_project !== "";

    // Address Information section
    const addressInfoComplete =
      lead?.address &&
      lead?.address !== "" &&
      lead?.state &&
      lead?.state !== "" &&
      lead?.city &&
      lead?.city !== "" &&
      lead?.country &&
      lead?.country !== "";

    setSectionCompletion({
      leadInformation: leadInfoComplete,
      serviceInformation: serviceInfoComplete,
      projectDetails: projectDetailsComplete,
      addressInformation: addressInfoComplete,
    });
  }, [lead]);

  // Update section completion when lead data changes
  useEffect(() => {
    checkSectionCompletion();
  }, [checkSectionCompletion]);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) {
      console.warn(`Section with id "${sectionId}" not found`);
      return;
    }

    // Use scrollIntoView with block start, which respects scroll-margin-top
    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Handle progress bar click to show/hide sections
  const handleProgressBarClick = (section) => {
    // SUPER_ADMIN can access all sections normally
    if (isSuperAdmin) {
      scrollToSection(section);
      return;
    }

    // Tele-Caller clicking on Sales Person section - Toggle Project Details (disabled)
    if (
      userInfo?.user_type === "Tele-Caller" &&
      section === "project-details"
    ) {
      setVisibleSections((prev) => {
        const currentState = prev.projectDetails;
        const newState = !currentState; // Toggle: true → false, false → true

        // Scroll only if showing (not if hiding)
        if (newState) {
          // Will show - scroll after state update
          setTimeout(() => {
            const element = document.getElementById("project-details");
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 100);
        }

        return {
          ...prev,
          projectDetails: newState,
        };
      });
      return; // Important: return to prevent further execution
    }

    // Sales Person clicking on Tele Caller Information section - Toggle Tele Caller Information (disabled)
    if (
      userInfo?.user_type === "Sales Person" &&
      section === "lead-information"
    ) {
      setVisibleSections((prev) => {
        const currentState = prev.teleCallerInfo;
        const newState = !currentState; // Toggle: true → false, false → true

        console.log(
          "Toggle Tele Caller Info - Before:",
          currentState,
          "After:",
          newState
        );

        // Scroll only if showing (not if hiding)
        if (newState) {
          // Will show - scroll after state update
          setTimeout(() => {
            const element = document.getElementById("lead-information");
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 100);
        }

        return {
          ...prev,
          teleCallerInfo: newState,
        };
      });
      return; // Important: return to prevent further execution
    }

    // Normal scroll for own sections
    scrollToSection(section);
  };

  // Detect which section is being edited
  const detectActiveSection = (fieldName) => {
    // Lead Information fields
    if (
      [
        "lead_name",
        "phone",
        "email",
        "whatsapp_no",
        "owner_ids",
        "leadsource",
        "background",
      ].includes(fieldName)
    ) {
      setActiveSection("lead-information");
      setTimeout(() => setActiveSection(null), 2000); // Stop blinking after 2 seconds
    }
    // Service Information fields
    else if (
      ["product_id", "qty", "accounts", "contact_id"].includes(fieldName)
    ) {
      setActiveSection("service-information");
      setTimeout(() => setActiveSection(null), 2000);
    }
    // Project Details fields
    else if (
      [
        "leadtype",
        // "lead_status",
        "on_site_contact_person_no",
        "no_of_floors",
        "type_of_roof",
        "dcr",
        "plant_capacity_required_by_client",
        "type_of_connection",
        "sanction_load",
        "average_monthly_bill",
        "type_of_project",
      ].includes(fieldName)
    ) {
      setActiveSection("project-details");
      setTimeout(() => setActiveSection(null), 2000);
    }
    // Address Information fields
    else if (
      [
        "location_link",
        "address",
        "state",
        "city",
        "pincode",
        "country",
      ].includes(fieldName)
    ) {
      setActiveSection("address-information");
      setTimeout(() => setActiveSection(null), 2000);
    }
  };

  // Normalize value from native select or React Select (object with .value) - used in handleChange
  const getNormalizedValue = (val) =>
    val != null && typeof val === "object" && "value" in val ? val.value : val;

  // Clear main-form leadtype error when at least one project row has leadtype (Sales Person)
  useEffect(() => {
    if (
      (isSuperAdmin || userInfo?.user_type === "Sales Person") &&
      projectLineItems?.some(
        (r) => getNormalizedValue(r?.leadtype) && String(getNormalizedValue(r?.leadtype)).trim() !== ""
      )
    ) {
      setErrors((prev) => {
        if (!prev?.leadtype) return prev;
        const next = { ...prev };
        delete next.leadtype;
        return next;
      });
    }
  }, [projectLineItems, isSuperAdmin, userInfo?.user_type]);

  // onChange Handler
  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    const displayVal = value == null ? "null" : Array.isArray(value) ? `[${value?.length} items]` : `'${value}'`;
    console.log(`[Lead Field Change] ${name}:`, displayVal, "(from form input)");
    if (["leadtype"].includes(name)) {
      value = getNormalizedValue(value) ?? value;
      if (typeof value === "string") value = value.trim();
    }
    // Detect active section for blink effect
    detectActiveSection(name);

    // Tele Caller Information fields - only validate for Tele-Caller and SUPER_ADMIN
    if (name == "lead_name") {
      // Skip validation for Sales Person (not SUPER_ADMIN)
      if (userInfo?.user_type === "Sales Person" && !isSuperAdmin) {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      } else {
        if (value == "") {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: validationMessages.leadName.required },
          }));
        } else if (
          e?.target?.pattern &&
          !RegExp(e.target.pattern).test(value)
        ) {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: validationMessages.leadName.invalid },
          }));
        } else {
          setErrors((prev) => {
            delete prev[name];
            return { ...prev };
          });
        }
      }
    } else if (name == "leadtype") {
      if (isSuperAdmin || userInfo?.user_type === "Sales Person") {
        if (value == "" || (typeof value === "string" && value.trim() === "")) {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: validationMessages.leadType.required },
          }));
        } else {
          setErrors((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
          });
        }
      } else {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    } else if (name == "email") {
      if (
        value != "" &&
        e?.target?.pattern &&
        !RegExp(e.target.pattern).test(value)
      ) {
        setErrors((prev) => ({
          ...prev,
          [name]: { message: validationMessages.email.invalid },
        }));
      } else {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
    } else if (name == "phone") {
      // Auto-fill WhatsApp Number with Phone value if WhatsApp Number is empty or same as Phone
      const shouldUpdateWhatsApp = value && (
        lead?.whatsapp_no === "" ||
        !lead?.whatsapp_no ||
        lead?.whatsapp_no === lead?.phone
      );
      // Sync phone to on_site_contact_person_no so it appears there and stays editable
      if (shouldUpdateWhatsApp) {
        setLead((prev) => ({ ...prev, [name]: value, whatsapp_no: value, on_site_contact_person_no: value }));
      } else {
        setLead((prev) => ({ ...prev, [name]: value, on_site_contact_person_no: value }));
      }

      // Skip validation for Sales Person (not SUPER_ADMIN)
      if (userInfo?.user_type === "Sales Person" && !isSuperAdmin) {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      } else {
        if (value == "") {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: validationMessages.phone.required },
          }));
        } else if (
          e?.target?.pattern &&
          !RegExp(e.target.pattern).test(value)
        ) {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: validationMessages.phone.invalid },
          }));
        } else {
          setErrors((prev) => {
            delete prev[name];
            return { ...prev };
          });
        }
      }
    } else if (name == "on_site_contact_person_no") {
      // Only validate for Sales Person and SUPER_ADMIN, not for Tele-Caller
      if (isSuperAdmin || userInfo?.user_type === "Sales Person") {
        if (value == "") {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: validationMessages.contact_no.required },
          }));
        } else if (
          e?.target?.pattern &&
          !RegExp(e.target.pattern).test(value)
        ) {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: validationMessages.contact_no.invalid },
          }));
        } else {
          setErrors((prev) => {
            delete prev[name];
            return { ...prev };
          });
        }
      } else {
        // For Tele-Caller, remove error if exists
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
    } else if (name == "whatsapp_no") {
      if (
        value != "" &&
        e?.target?.pattern &&
        !RegExp(e.target.pattern).test(value)
      ) {
        setErrors((prev) => ({
          ...prev,
          [name]: { message: validationMessages.whatsappNumber.invalid },
        }));
      } else {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
    } else if (name == "owner_ids") {
      if (!value?.length) {
        setErrors((prev) => ({
          ...prev,
          [name]: { message: validationMessages.ownerIds.required },
        }));
      } else {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
    } else if (name == "company_name") {
      if (
        value != "" &&
        e?.target?.pattern &&
        !RegExp(e.target.pattern).test(value)
      ) {
        setErrors((prev) => ({
          ...prev,
          [name]: { message: validationMessages.name.invalid },
        }));
      } else {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
    } else if (name == "is_gst") {
      value != "Yes" && setLead((pre) => ({ ...pre, gst_no: "" }));
    } else if (name == "customer_primary_phone") {
      if (
        value != "" &&
        e?.target?.pattern &&
        !RegExp(e.target.pattern).test(value)
      ) {
        setErrors((prev) => ({
          ...prev,
          [name]: { message: validationMessages.phone.invalid },
        }));
      } else {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
    } else if (name == "customer_secondary_phone") {
      if (
        value != "" &&
        e?.target?.pattern &&
        !RegExp(e.target.pattern).test(value)
      ) {
        setErrors((prev) => ({
          ...prev,
          [name]: { message: validationMessages.phone.invalid },
        }));
      } else {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
    } else if (name == "city") {
      // City is optional - no validation required
      setErrors((prev) => {
        delete prev[name];
        return { ...prev };
      });
    } else if (name == "pincode") {
      if (
        value != "" &&
        e?.target?.pattern &&
        !RegExp(e.target.pattern).test(value)
      ) {
        setErrors((prev) => ({
          ...prev,
          [name]: { message: validationMessages.pinCode.invalid },
        }));
      } else {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
    } else if (name == "leadsource") {
      // When leadsource changes: reset Reference By (contact_id), handle conditional fields
      if (value !== "Commission Agent") {
        setLead((prev) => ({
          ...prev,
          [name]: value,
          contact_id: "",
          applied: "",
        }));
        setAccountAmount("");
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors["applied"];
          delete newErrors["amount"];
          delete newErrors["contact_id"];
          return newErrors;
        });
      } else {
        setLead((prev) => ({ ...prev, [name]: value, contact_id: "" }));
        if (lead?.applied === "" || !lead?.applied) {
          setErrors((prev) => ({
            ...prev,
            applied: { message: "Please select applicable option" },
          }));
        }
        // Modified By Farhan Khan on 16-Feb-2026 (Reference By is no longer required - removed contact_id required validation for Commission Agent)
        // Reference By is optional for all lead sources
      }
      setValidated(true);
    } else if (name == "applied") {
      // Validate applied field when leadsource is "Commission Agent"
      if (lead?.leadsource === "Commission Agent") {
        if (value == "" || !value) {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: "Please select applicable option" },
          }));
        } else {
          setErrors((prev) => {
            delete prev[name];
            return { ...prev };
          });
        }
      }
      // If applied is not "Yes", clear amount field
      if (value !== "Yes") {
        setLead((prev) => ({
          ...prev,
          [name]: value,
        }));
        setAccountAmount(""); // Clear account amount
        // Clear amount error
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors["amount"];
          return newErrors;
        });
      } else {
        // If applied is "Yes", update lead and validate amount field if empty (in both Create and Edit modes)
        setLead((prev) => ({ ...prev, [name]: value }));
        // Validate amount field if it's empty
        if (accountAmount === "" || !accountAmount) {
          setErrors((prev) => ({
            ...prev,
            amount: { message: "Please provide commission amount" },
          }));
        }
      }
      setValidated(true);
    } else if (name == "amount") {
      // Update accountAmount state (not lead state)
      setAccountAmount(value);
      // Validate amount field when applied is "Yes" (in both Create and Edit modes)
      if (lead?.applied === "Yes") {
        if (value == "" || !value) {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: "Please provide commission amount" },
          }));
        } else if (
          e?.target?.pattern &&
          !RegExp(e.target.pattern).test(value)
        ) {
          setErrors((prev) => ({
            ...prev,
            [name]: { message: "Please provide valid commission amount" },
          }));
        } else {
          setErrors((prev) => {
            delete prev[name];
            return { ...prev };
          });
        }
      } else {
        setErrors((prev) => {
          delete prev[name];
          return { ...prev };
        });
      }
      setValidated(true);
    }

    // if (name == "lead_status" && value === "Lost") {
    //   setLead((pre) => ({ ...pre, is_lost_date: new Date() }));
    //   handleShow();
    // }

    // Modified By Farhan Khan on 16-Feb-2026 (Reference By is not required - removed contact_id required validation)
    if (name === "contact_id") {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      setValidated(true);
      setLead((prev) => ({ ...prev, [name]: value }));
    }

    // Only update lead state if not already handled above
    if (name !== "leadsource" && name !== "applied" && name !== "amount" && name !== "contact_id") {
      setValidated(true);
      setLead((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Field key -> display name for validation toasts (format: "<Field Name> is Required")
  const REQUIRED_FIELD_LABELS = {
    lead_name: "Lead Name",
    phone: "Phone",
    leadtype: "Lead Type",
    // lead_status: "Lead Status",
    on_site_contact_person_no: "On-site Contact Person No",
    owner_ids: "Assign Users",
    applied: "Commission Applicable",
    amount: "Commission Amount",
    contact_id: "Referred By",
  };
  // Project row field labels for validation toasts
  const PROJECT_ROW_FIELD_LABELS = {
    site_name: "Site Name",
    leadtype: "Lead Type",
    on_site_contact_person_no: "On-site Contact Person No",
    address: "Address",
    state: "State",
    city: "City",
    pincode: "Pincode",
    sanction_load: "Sanction Load",
    plant_capacity_required_by_client: "Plant Capacity Required",
  };

  // field validators
  const checkRequiredFields = () => {
    let isError = false;
    let firstErrorKey = null;

    // For Sales Person (not SUPER_ADMIN), immediately clear any Tele Caller Information errors
    if (userInfo?.user_type === "Sales Person" && !isSuperAdmin) {
      const teleCallerInfoFields = [
        "lead_name",
        "leadtype",
        "phone",
        // "lead_status",
      ];
      teleCallerInfoFields.forEach((field) => {
        if (errors[field]) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        }
      });
      // Clear leadtype error when at least one project row has leadtype
      const hasLeadTypeInRows = projectLineItems?.some(
        (r) => getNormalizedValue(r?.leadtype) && String(getNormalizedValue(r?.leadtype)).trim() !== ""
      );
      if (hasLeadTypeInRows && errors?.leadtype) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.leadtype;
          return next;
        });
      }
    }

    if (lead?.salutation) {
    }

    // Tele Caller Information fields - only validate for Tele-Caller and SUPER_ADMIN
    // IMPORTANT: Skip ALL validation for Sales Person - these fields are not required for them
    if (isSuperAdmin || userInfo?.user_type === "Tele-Caller") {
      // Validate lead_name - required for Tele-Caller and SUPER_ADMIN
      if (lead?.lead_name !== undefined) {
        if (lead?.lead_name == "" || !lead?.lead_name) {
          setErrors((prev) => ({
            ...prev,
            lead_name: { message: validationMessages.leadName.required },
          }));
          isError = true;
        } else if (
          regExPattern?.name &&
          !RegExp(regExPattern.name).test(lead.lead_name)
        ) {
          setErrors((prev) => ({
            ...prev,
            lead_name: { message: validationMessages.leadName.invalid },
          }));
          isError = true;
        } else {
          setErrors((prev) => {
            delete prev["lead_name"];
            return { ...prev };
          });
        }
      }

      // Validate phone - required for Tele-Caller and SUPER_ADMIN
      if (lead?.phone !== undefined) {
        if (lead?.phone == "" || !lead?.phone) {
          setErrors((prev) => ({
            ...prev,
            phone: { message: validationMessages.phone.required },
          }));
          isError = true;
        } else if (
          regExPattern?.phone &&
          !RegExp(regExPattern.phone).test(lead.phone)
        ) {
          setErrors((prev) => ({
            ...prev,
            phone: { message: validationMessages.phone.invalid },
          }));
          isError = true;
        } else {
          setErrors((prev) => {
            delete prev["phone"];
            return { ...prev };
          });
        }
      }
    }
    // For Sales Person, Tele Caller Information fields are already cleared at the start of this function
    // Note: For Sales Person, Tele Caller Information fields are already cleared at the start of this function
    // Project Details fields validation - for Sales Person and SUPER_ADMIN
    if (isSuperAdmin || userInfo?.user_type === "Sales Person") {
      // Validate leadtype - required for Sales Person and SUPER_ADMIN
      // if (lead?.leadtype !== undefined) {
      //   if (lead?.leadtype == "" || !lead?.leadtype) {
      //     setErrors((prev) => ({
      //       ...prev,
      //       leadtype: { message: validationMessages.leadType.required },
      //     }));
      //     isError = true;
      //   } else {
      //     setErrors((prev) => {
      //       delete prev["leadtype"];
      //       return { ...prev };
      //     });
      //   }
      // }

      // Validate on_site_contact_person_no
      if (lead?.on_site_contact_person_no) {
        if (lead?.on_site_contact_person_no == "") {
          if (!firstErrorKey) firstErrorKey = "on_site_contact_person_no";
          setErrors((prev) => ({
            ...prev,
            on_site_contact_person_no: {
              message: validationMessages.contact_no.required,
            },
          }));
          isError = true;
        } else if (
          regExPattern?.phone &&
          !RegExp(regExPattern.phone).test(lead.on_site_contact_person_no)
        ) {
          if (!firstErrorKey) firstErrorKey = "on_site_contact_person_no";
          setErrors((prev) => ({
            ...prev,
            on_site_contact_person_no: {
              message: validationMessages.contact_no.invalid,
            },
          }));
          isError = true;
        } else {
          setErrors((prev) => {
            delete prev["on_site_contact_person_no"];
            return { ...prev };
          });
        }
      }
    }

    // For Tele-Caller (not SUPER_ADMIN), remove Project Details field errors if they exist
    if (userInfo?.user_type === "Tele-Caller" && !isSuperAdmin) {
      const projectDetailFields = [
        "leadtype",
        // "lead_status",
        "on_site_contact_person_no",
        "no_of_floors",
        "type_of_roof",
        "dcr",
        "plant_capacity_required_by_client",
        "type_of_connection",
        "sanction_load",
        "average_monthly_bill",
        "type_of_project",
      ];
      projectDetailFields.forEach((field) => {
        if (errors[field]) {
          setErrors((prev) => {
            delete prev[field];
            return { ...prev };
          });
        }
      });
    }

    if (lead?.email) {
      if (
        lead?.email != "" &&
        regExPattern?.email &&
        !RegExp(regExPattern.email).test(lead.email)
      ) {
        setErrors((prev) => ({
          ...prev,
          email: { message: validationMessages.email.invalid },
        }));
        isError = true;
      } else {
        setErrors((prev) => {
          delete prev["email"];
          return { ...prev };
        });
      }
    }

    if (lead?.whatsapp_no) {
      if (
        lead?.whatsapp_no != "" &&
        regExPattern?.whatsapp_no &&
        !RegExp(regExPattern.whatsapp_no).test(lead.whatsapp_no)
      ) {
        setErrors((prev) => ({
          ...prev,
          whatsapp_no: { message: validationMessages.whatsappNumber.invalid },
        }));
        isError = true;
      } else {
        setErrors((prev) => {
          delete prev["whatsapp_no"];
          return { ...prev };
        });
      }
    }

    // owner_ids validation - required for ALL users (Tele-Caller, Sales Person, SUPER_ADMIN)
    // Always validate owner_ids regardless of user type
    if (!lead?.owner_ids || !lead?.owner_ids?.length) {
      setErrors((prev) => ({
        ...prev,
        owner_ids: { message: validationMessages.ownerIds.required },
      }));
      isError = true;
    } else {
      setErrors((prev) => {
        delete prev["owner_ids"];
        return { ...prev };
      });
    }

    // Validate "applied" field - required when leadsource is "Commission Agent"
    if (lead?.leadsource === "Commission Agent") {
      if (lead?.applied === "" || !lead?.applied) {
        if (!firstErrorKey) firstErrorKey = "applied";
        setErrors((prev) => ({
          ...prev,
          applied: { message: "Please select applicable option" },
        }));
        isError = true;
      } else {
        setErrors((prev) => {
          delete prev["applied"];
          return { ...prev };
        });
      }
    } else {
      // Clear applied error if leadsource is not "Commission Agent"
      setErrors((prev) => {
        delete prev["applied"];
        return { ...prev };
      });
    }

    // Validate "amount" field - required when applied is "Yes" (in both Create and Edit modes)
    if (lead?.applied === "Yes") {
      if (accountAmount === "" || !accountAmount) {
        if (!firstErrorKey) firstErrorKey = "amount";
        setErrors((prev) => ({
          ...prev,
          amount: { message: "Please provide commission amount" },
        }));
        isError = true;
      } else if (
        regExPattern?.amount &&
        !RegExp(regExPattern.amount).test(accountAmount)
      ) {
        setErrors((prev) => ({
          ...prev,
          amount: { message: "Please provide valid commission amount" },
        }));
        isError = true;
      } else {
        setErrors((prev) => {
          delete prev["amount"];
          return { ...prev };
        });
      }
    } else {
      // Clear amount error if applied is not "Yes"
      setErrors((prev) => {
        delete prev["amount"];
        return { ...prev };
      });
    }

    // Modified By Farhan Khan on 16-Feb-2026 (Reference By is not required - removed contact_id validation)
    setErrors((prev) => {
      const next = { ...prev };
      delete next["contact_id"];
      return next;
    });

    // if (lead?.customer_type) {
    //   if (lead?.customer_type == "") {
    //     setErrors((prev) => ({
    //       ...prev,
    //       customer_type: { message: validationMessages.customerType.required },
    //     }));
    //     isError = true;
    //   } else {
    //     setErrors((prev) => {
    //       delete prev["customer_type"];
    //       return { ...prev };
    //     });
    //   }
    // }

    if (lead?.company_name) {
      if (
        lead?.company_name != "" &&
        regExPattern?.name &&
        !RegExp(regExPattern.name).test(lead.company_name)
      ) {
        setErrors((prev) => ({
          ...prev,
          company_name: { message: validationMessages.name.invalid },
        }));
        isError = true;
      } else {
        setErrors((prev) => {
          delete prev["company_name"];
          return { ...prev };
        });
      }
    }

    if (lead?.customer_primary_phone) {
      if (
        lead?.customer_primary_phone != "" &&
        regExPattern?.phone &&
        !RegExp(regExPattern.phone).test(lead.customer_primary_phone)
      ) {
        setErrors((prev) => ({
          ...prev,
          customer_primary_phone: { message: validationMessages.phone.invalid },
        }));
        isError = true;
      } else {
        setErrors((prev) => {
          delete prev["customer_primary_phone"];
          return { ...prev };
        });
      }
    }

    if (lead?.customer_secondary_phone) {
      if (
        lead?.customer_secondary_phone != "" &&
        regExPattern?.phone &&
        !RegExp(regExPattern.phone).test(lead.customer_secondary_phone)
      ) {
        setErrors((prev) => ({
          ...prev,
          customer_secondary_phone: {
            message: validationMessages.phone.invalid,
          },
        }));
        isError = true;
      } else {
        setErrors((prev) => {
          delete prev["customer_secondary_phone"];
          return { ...prev };
        });
      }
    }

    // City is optional - no validation required
    // Remove city error if exists
    if (errors["city"]) {
      setErrors((prev) => {
        delete prev["city"];
        return { ...prev };
      });
    }

    if (lead?.pincode) {
      if (
        lead?.pincode != "" &&
        regExPattern?.pinCode &&
        !RegExp(regExPattern.pinCode).test(lead.pincode)
      ) {
        setErrors((prev) => ({
          ...prev,
          pincode: { message: validationMessages.pinCode.invalid },
        }));
        isError = true;
      } else {
        setErrors((prev) => {
          delete prev["pincode"];
          return { ...prev };
        });
      }
    }

    // Tele Caller Information fields - only validate for Tele-Caller and SUPER_ADMIN
    // IMPORTANT: Skip validation for Sales Person
    if (isSuperAdmin || userInfo?.user_type === "Tele-Caller") {
      if (!lead?.lead_name) {
        if (!firstErrorKey) firstErrorKey = "lead_name";
        setErrors((prev) => ({
          ...prev,
          lead_name: { message: validationMessages.leadName.required },
        }));
        isError = true;
      }
      if (!lead?.phone) {
        if (!firstErrorKey) firstErrorKey = "phone";
        setErrors((prev) => ({
          ...prev,
          phone: { message: validationMessages.phone.required },
        }));
        isError = true;
      }
    }

    // Project Details fields - validate leadtype ONLY when project rows exist (no cross-trigger on Lead fields)
    if (isSuperAdmin || userInfo?.user_type === "Sales Person") {
      const projectRows = projectLineItems || [];
      if (projectRows.length === 0) {
        // No project rows: clear leadtype error, do not require it
        if (errors?.leadtype) {
          setErrors((prev) => {
            const next = { ...prev };
            delete next.leadtype;
            return next;
          });
        }
      } else {
        // Project rows exist: run leadtype validation as before
        const leadtypeVal = getNormalizedValue(lead?.leadtype);
        const hasLeadTypeInRows = projectRows.some(
          (r) => getNormalizedValue(r?.leadtype) && String(getNormalizedValue(r?.leadtype)).trim() !== ""
        );
        if (!leadtypeVal && !hasLeadTypeInRows) {
          if (!firstErrorKey) firstErrorKey = "leadtype";
          setErrors((prev) => ({
            ...prev,
            leadtype: { message: validationMessages.leadType.required },
          }));
          isError = true;
        }
      }
    }
    // Lead Status is OPTIONAL - clear any lead_status error so it does not block save
    // if (errors?.lead_status) {
    //   setErrors((prev) => {
    //     const next = { ...prev };
    //     delete next["lead_status"];
    //     return next;
    //   });
    // }

    // Common fields - required for ALL users (Tele-Caller, Sales Person, SUPER_ADMIN)
    if (!lead?.owner_ids?.length) {
      if (!firstErrorKey) firstErrorKey = "owner_ids";
      setErrors((prev) => ({
        ...prev,
        owner_ids: { message: validationMessages.ownerIds.required },
      }));
      isError = true;
    }
    // if (!lead?.customer_type) {
    //   setErrors((prev) => ({
    //     ...prev,
    //     customer_type: { message: validationMessages.customerType.required },
    //   }));
    //   isError = true;
    // }
    // City is optional - removed validation
    // Remove city error if exists
    if (errors["city"]) {
      setErrors((prev) => {
        delete prev["city"];
        return { ...prev };
      });
    }

    return isError ? firstErrorKey : null;
  };

  // onSubmit handler
  const handleSubmit = async (e) => {
    console.log("Called ?");
    e.preventDefault();
    // if (isSubmitting) return;
    try {
      // setIsSubmitting(true);
      // For Sales Person, immediately clear any Tele Caller Information errors before validation
      // if (userInfo?.user_type === "Sales Person" && !isSuperAdmin) {
      //   const teleCallerInfoFields = [
      //     "lead_name",
      //     "phone",
      //   ];
      //   setErrors((prev) => {
      //     const newErrors = { ...prev };
      //     let hasChanges = false;
      //     teleCallerInfoFields.forEach((field) => {
      //       if (newErrors[field]) {
      //         delete newErrors[field];
      //         hasChanges = true;
      //       }
      //     });
      //     // When no project rows, clear leadtype error (project validation must not affect Lead fields)
      //     if ((projectLineItems || []).length === 0 && newErrors.leadtype) {
      //       delete newErrors.leadtype;
      //       hasChanges = true;
      //     }
      //     return hasChanges ? newErrors : prev;
      //   });
      // }

      console.log("lead", lead);
      console.log("errors", errors);
      const firstErrorKey = checkRequiredFields();
      // console.log("[Lead Save Debug] Validation result - firstErrorKey:", firstErrorKey, firstErrorKey ? "(BLOCKED - missing/invalid required field)" : "(PASSED - all required fields OK)");
      if (firstErrorKey) {
        setValidated(true);
        const fieldLabel = REQUIRED_FIELD_LABELS[firstErrorKey] || firstErrorKey.replace(/_/g, " ");
        const toastMessage = `${fieldLabel} is Required`;
        console.log("[Lead Save Debug] Toast fired (Lead Form validation):", toastMessage);
        PubSub.publish("RECORD_ERROR_TOAST", {
          message: toastMessage,
        });
        return;
      }
      // console.log("lead", lead);
      // console.log("errors", errors);
      // Exclude leadtype from blocking when no project rows (project validation must not affect Lead fields)
      const errKeys = Object?.keys(errors)?.filter((k) => errors[k]) || [];
      const hasBlockingErrors =
        (projectLineItems || []).length === 0
          ? errKeys.filter((k) => k !== "leadtype").length !== 0
          : errKeys.length !== 0;
      if (hasBlockingErrors) {
        setValidated(true);
        return;
      }
      // Validate project rows ONLY if user has added at least one project row (via Add Row or loaded from edit)
      const projectRowErrMap = {};
      let hasProjectRowError = false;
      if ((projectLineItems || []).length > 0) {
        (projectLineItems || []).forEach((row, rowIndex) => {
          if (!isRowFilled(row)) return;
          const rowErrs = validateProjectRowForAddRow(row);
          if (Object.keys(rowErrs).length > 0) {
            projectRowErrMap[rowIndex] = rowErrs;
            hasProjectRowError = true;
          }
        });
      }
      if (hasProjectRowError) {
        console.log("[Lead Save Debug] Project row validation result: BLOCKED", projectRowErrMap);
        setValidated(true);
        setProjectRowValidationErrors(projectRowErrMap);
        // Get first error from first errored row for toast
        const firstRowIndex = Object.keys(projectRowErrMap)[0];
        const firstRowErrs = projectRowErrMap[firstRowIndex] || {};
        const firstErrField = Object.keys(firstRowErrs)[0];
        const firstErrMsg = firstErrField ? firstRowErrs[firstErrField] : "";
        const fieldLabel = PROJECT_ROW_FIELD_LABELS[firstErrField] || (firstErrField && firstErrField.replace(/_/g, " "));
        const isRequiredStyle = firstErrMsg && (String(firstErrMsg).toLowerCase().includes("provide") || String(firstErrMsg).toLowerCase().includes("required"));
        const toastMessage = isRequiredStyle && fieldLabel ? `${fieldLabel} is Required` : (firstErrMsg || "Please fix project row validation errors");
        console.log("[Lead Save Debug] Toast fired (Project Row validation):", toastMessage);
        PubSub.publish("RECORD_ERROR_TOAST", {
          message: toastMessage,
        });
        return;
      }
      console.log("[Lead Save Debug] All validations passed - proceeding to API call");
      // copy the obj to send as payload
      const record = { ...lead };

      //Delete Extra Fields from Lead JSON.
      console.log("record.contact_name", record.contact_name);


      //  BETTER (extract only name part)
      var contactName = record.contact_name
        ? record.contact_name.split(" - ")[0].trim()
        : "";
      delete record.owner_name;
      delete record.contact_name;
      delete record.product_name;
      delete record.project_line_items;

      //End Here Delete Extra Fields from Lead JSON.

      if (record?.date_of_birth) {
        record.date_of_birth = moment(record.date_of_birth).format(
          "DD MMM, yyyy"
        );
      }
      if (record?.anniversary_date) {
        record.anniversary_date = moment(record.anniversary_date).format(
          "DD MMM, yyyy"
        );
      }
      if (record?.is_lost_date) {
        record.is_lost_date = moment(record.is_lost_date).format("DD MMM, yyyy");
      }

      // handle numeric fields
      record.qty = record?.qty == "" ? 0 : record.qty;
      // record.lead_status = getNormalizedValue(record?.lead_status) || record?.lead_status || "Enquiry";
      record.leadtype = getNormalizedValue(record?.leadtype) || record?.leadtype;

      // handle date fields
      record.date_of_birth =
        record?.date_of_birth == "" ? null : record.date_of_birth;
      record.anniversary_date =
        record?.anniversary_date == "" ? null : record.anniversary_date;
      record.is_lost_date =
        record?.is_lost_date == "" ? null : record.is_lost_date;

      record.customer_site_visit_date =
        record?.customer_site_visit_date == ""
          ? null
          : record.customer_site_visit_date;
      delete record.owner_name;

      // Modified By Farhan Khan on 16-Feb-2026 (Handled empty UUID issue to prevent PostgreSQL error)
      if (record.contact_id == null || record.contact_id === "" || String(record.contact_id).trim() === "") {
        record.contact_id = null;
      }
      // End Here Comment By Farhan Khan on 16-Feb-2026

      // handel account_id make
      record.account_id = record.accounts
        ?.filter((item) => item.value)
        ?.map((item) => item.value);
      delete record.accounts;

      // handel owner_ids
      record.owner_ids = record.owner_ids
        ?.filter((item) => item.value)
        ?.map((item) => item.value);
      // //========= Logic to perform Create or Edit ======
      if (record.id) {
        // console.log("Updated");
        console.log("Called ? Update");
        const result = await solisPowerApi.saveLead(record);
        console.log("first", result);
        if (result?.success) {
          if (Array.isArray(projectLineItems) && projectLineItems.length > 0) {
            const normalizedItems = projectLineItems
              .filter((row) => isRowFilled(row))
              .map((row) => ({
                ...row,
                leadtype: getNormalizedValue(row?.leadtype) ?? row?.leadtype ?? "",
                // lead_status: getNormalizedValue(row?.lead_status) ?? row?.lead_status ?? "",
                project_status: getNormalizedValue(row?.project_status) ?? row?.project_status ?? "",
                state: getNormalizedValue(row?.state) ?? row?.state ?? "",
                plant_capacity_required_by_client: row.plant_capacity_required_by_client != null && String(row.plant_capacity_required_by_client).trim() !== "" ? sanitizeNumericInput(row.plant_capacity_required_by_client) : (row.plant_capacity_required_by_client ?? ""),
                sanction_load: row.sanction_load != null && String(row.sanction_load).trim() !== "" ? sanitizeNumericInput(row.sanction_load) : (row.sanction_load ?? ""),
              }));
            const lineResult = await solisPowerApi.saveLeadProjectLineItems(record.id, normalizedItems.length > 0 ? normalizedItems : []);
            if (lineResult?.errors) {
              PubSub.publish("RECORD_ERROR_TOAST", {
                title: "Project rows",
                message: lineResult.errors?.message || lineResult.errors || "Failed to save project rows",
              });
            }
          } else {
            await solisPowerApi.saveLeadProjectLineItems(record.id, []);
          }
          PubSub.publish("RECORD_SAVED_TOAST", {
            title: "Record Update",
            message: `${result?.message ? result?.message : "Record updated successfully"
              }`,
          });
          // navigate(`/leads/${record.id}`, { state: record });
          navigate(`${ROUTES.LEAD.LIST}/${record.id}`, { state: record });
        } else {
          const errMsg = typeof result?.errors === "string" ? result.errors : result?.errors;
          if (errMsg && String(errMsg).includes("phone number already exists")) {
            setErrors((prev) => ({
              ...prev,
              phone: { message: "Lead phone number already exists" },
            }));
          }
          PubSub.publish("RECORD_ERROR_TOAST", {
            title: "Error found",
            message: `${errMsg ? errMsg : "Some error found"}`,
          });
        }
      } else {
        // console.log("Created");
        console.log("Called ? Create");
        const result = await solisPowerApi.createLead(record);
        console.log("first", result);
        if (result?.success) {
          const newLeadId = result?.data?.id;
          if (newLeadId && Array.isArray(projectLineItems) && projectLineItems.length > 0) {
            const normalizedItems = projectLineItems
              .filter((row) => isRowFilled(row))
              .map((row) => ({
                ...row,
                leadtype: getNormalizedValue(row?.leadtype) ?? row?.leadtype ?? "",
                // lead_status: getNormalizedValue(row?.lead_status) ?? row?.lead_status ?? "",
                project_status: getNormalizedValue(row?.project_status) ?? row?.project_status ?? "",
                state: getNormalizedValue(row?.state) ?? row?.state ?? "",
                plant_capacity_required_by_client: row.plant_capacity_required_by_client != null && String(row.plant_capacity_required_by_client).trim() !== "" ? sanitizeNumericInput(row.plant_capacity_required_by_client) : (row.plant_capacity_required_by_client ?? ""),
                sanction_load: row.sanction_load != null && String(row.sanction_load).trim() !== "" ? sanitizeNumericInput(row.sanction_load) : (row.sanction_load ?? ""),
              }));
            if (normalizedItems.length > 0) {
              const lineResult = await solisPowerApi.saveLeadProjectLineItems(newLeadId, normalizedItems);
              if (lineResult?.errors) {
                PubSub.publish("RECORD_ERROR_TOAST", {
                  title: "Project rows",
                  message: lineResult.errors?.message || lineResult.errors || "Failed to save project rows",
                });
              }
            }
          }
          // If amount is filled, create Account
          console.log("Account creation check:", {
            accountAmount,
            applied: lead?.applied,
            contact_id: lead?.contact_id,
          });

          if (accountAmount && accountAmount !== "" && lead?.applied === "Yes") {
            try {
              // Fetch existing accounts to get next number
              const existingAccounts = await solisPowerApi.fetchAccounts();
              let nextNumber = 1;

              if (existingAccounts && Array.isArray(existingAccounts)) {
                // Extract numbers from account names like "Acc1", "Acc2", etc.
                const accountNumbers = existingAccounts
                  .map((acc) => {
                    if (acc.name && acc.name.startsWith("Acc")) {
                      const num = parseInt(acc.name.replace("Acc", ""));
                      return isNaN(num) ? 0 : num;
                    }
                    return 0;
                  })
                  .filter((num) => num > 0);

                if (accountNumbers.length > 0) {
                  nextNumber = Math.max(...accountNumbers) + 1;
                }
              }

              // Create account object
              const commisssionData = {
                firstname: contactName,
                amount: accountAmount,
              };

              // Add contact_id only if it exists (from Referred By)
              if (record?.contact_id) {
                commisssionData.contact_id = record?.contact_id;
              }

              // Add lead_id only if it exists (from created lead)
              if (result?.data?.id) {
                commisssionData.lead_id = result?.data?.id;
              }

              // Create account
              const accountResult = await solisPowerApi.createCommission(
                commisssionData
              );
              if (accountResult?.success) {
                console.log("Account created successfully:", accountResult);
                PubSub.publish("RECORD_SAVED_TOAST", {
                  title: "Commission Created",
                  message: `Commission ${commisssionData.firstname} created successfully`,
                });
              } else {
                console.error("Commission creation failed:", accountResult?.errors);
                PubSub.publish("RECORD_ERROR_TOAST", {
                  title: "Commission Creation Error",
                  message: accountResult?.errors || "Failed to create commission",
                });
              }
            } catch (error) {
              console.error("Error creating commission:", error);
              PubSub.publish("RECORD_ERROR_TOAST", {
                title: "Commission Creation Error",
                message: "Failed to create commission",
              });
            }
          } else {
            console.log("Account not created - conditions not met:", {
              hasAmount: accountAmount && accountAmount !== "",
              applied: lead?.applied === "Yes",
            });
          }

          PubSub.publish("RECORD_SAVED_TOAST", {
            title: "Record created",
            message: "Record created successfully",
          });
          // console.log("result", result);
          navigate(`${ROUTES.LEAD.LIST}/${result?.data?.id}`, {
            state: result?.data,
          });
        } else {
          const errMsg = typeof result?.errors === "string" ? result.errors : result?.errors;
          if (errMsg && String(errMsg).includes("phone number already exists")) {
            setErrors((prev) => ({
              ...prev,
              phone: { message: "Lead phone number already exists" },
            }));
          }
          PubSub.publish("RECORD_ERROR_TOAST", {
            title: "Error found",
            message: `${errMsg ? errMsg : "Some error found"}`,
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  //
  const handleCancel = () => {
    // setIsSubmitting(false);
    if (lead.id) {
      navigate(`${ROUTES.LEAD.LIST}/${lead.id}`, { state: lead });
    } else {
      navigate(ROUTES.LEAD.LIST);
    }
  };

  // const handleAssignedUserChange = (e) => {
  //   handleChange({ target: { name: "owner_id", value: e.value } });
  //   handleChange({ target: { name: "owner_name", value: e.label } });
  //   // const delegated_users = assignUsers?.filter((item) => item.managerid == e.value)?.map((item) => ({ value: item.id, label: item.username }));
  //   // if (lead.id) {
  //   //   if (lead.delegated_users && Array.isArray(lead.delegated_users)) {
  //   //     setLead((prev) => ({ ...prev, delegated_users: [...delegated_users, ...lead.delegated_users] }));
  //   //   } else {
  //   //     setLead((prev) => ({ ...prev, delegated_users: [...delegated_users] }));
  //   //   }
  //   // } else {
  //   // setLead((prev) => ({ ...prev, delegated_users: [...delegated_users] }));
  //   // }
  // };

  // SUPER_ADMIN can edit all fields, no restrictions
  const isDisabled = false;
  // !isSuperAdmin && userInfo?.user_type === "Sales Person";
  const isDisabledSalesPeron = false;
  // !isSuperAdmin && userInfo?.user_type === "Tele-Caller";

  // Added By Jaseem Hussain on 04-July-2025 (Contact Model to show for Create).
  const [showContactModel, setContactModel] = useState(false); //Contat Model Show State Added By Jaseem Hussain
  const [contactRef, setContactRecord] = useState({});
  const handleContactModel = () => {
    console.log("handleContactModel Call");
    setContactModel(true);
    console.log("handleContactModel Call End");
  };

  const handleContactModelClose = () => {
    setContactModel(false);
  };

  const handleChangeContactPartner = (e) => {
    setContactRecord({ ...contactRef, [e.target.name]: e.target.value });
  };

  const handleSubmitContact = async (e) => {
    let result = {};
    // contactRef.title = "Commission Agent";
    contactRef.lastname = "";
    result = await solisPowerApi.createContact(contactRef);
    if (result?.success) {
      setLead({
        ...lead,
        contact_id: result.data.id,
        contact_name: `${result?.data?.firstname || ""} ${result?.data?.lastname || ""
          }`,
        // contact_name: result.data.firstname + result.data.lastname,
        phone: result.data.phone,
      });
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "Record Update",
        message: "Record Saved successfully",
      });
      setContactModel(false);
      // Modified By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering - refresh full contacts list after new contact)
      fetchAllContactsForReference();
      // End Here Comment By Farhan Khan on 16-Feb-2026
    } else {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Record Update Error",
        message: result?.errors,
      });
    }
  };
  //End Here By Jaseem Hussain Khan on 04-July-2025

  //================================================

  const CHECK_ICON = (
    <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
      <path
        d="M13.5 4.5L6 12L2.5 8.5"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const CROSS_ICON = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#dc3545" opacity="0.1" />
      <path
        d="M8 8L16 16M16 8L8 16"
        stroke="#dc3545"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div className="lead-edit-layout">
      <Container className="pb-5 lead-edit-container">
        <>
          <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>REASON OF LOST LEAD</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Col lg={12}>
                <Form.Group>
                  <Form.Label>Lost Date</Form.Label>
                  <Form.Control
                    disabled
                    value={formatDateForCreatedDate(lead?.is_lost_date)}
                  />
                </Form.Group>
              </Col>
              <Col lg={12} className="mt-2">
                <Form.Group>
                  <Form.Label>Lost Reason</Form.Label>
                  <Form.Control
                    type="text"
                    name="lead_lost_remark"
                    placeholder="Please Enter Reason"
                    value={lead?.lead_lost_remark || ""} //lead?.remark
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col lg={12} className="mt-2">
                <Form.Group>
                  <Form.Label>Lost By (Company)</Form.Label>
                  <Form.Control
                    type="text"
                    name="lost_by_reason"
                    placeholder="Please Enter Lost By"
                    value={lead?.lost_by_reason || ""} //lead?.remark
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              {/* I will not close if you click outside me. Do not even try to press
          escape key. */}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={handleClose}>
                Save
              </Button>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              {/* <Button variant="primary">Understood</Button> */}
            </Modal.Footer>
          </Modal>
        </>

        <Row className="lead-edit-header-row">
          <Col md={12} className="flex-shrink-0">
            <Breadcrumb>
              <Breadcrumb.Item onClick={() => navigate(ROUTES.HOME)}>
                Home
              </Breadcrumb.Item>
              <Breadcrumb.Item onClick={() => navigate(ROUTES.LEAD.LIST)}>
                Leads
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>

          {/* Department section – scrolls with form (not sticky) */}
          <Col md={12} className="align-items-center flex-shrink-0" style={{
            position: "sticky",
            top: "-20px",
            alignSelf: "flex-start",
            zIndex: 999
          }}>
            {/* Progress Bar */}
            {/* <div className="section-progress-bar">
            <div className="progress-bar-container">
              <div
                className={`progress-step ${
                  sectionCompletion.leadInformation ? "completed" : ""
                } ${
                  activeSection === "lead-information" ? "active-blink" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleProgressBarClick("lead-information");
                }}
              >

                <div className="progress-step-circle">
                  {sectionCompletion.leadInformation ? (
                    // Completed → checkmark
                    <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.5 4.5L6 12L2.5 8.5"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    // Not completed → cross icon (modern design)
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        fill="#dc3545"
                        opacity="0.1"
                      />
                      <path
                        d="M8 8L16 16M16 8L8 16"
                        stroke="#dc3545"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
                <div className="progress-step-label">Tele Department</div>
              </div>
              <div
                className={`progress-line ${
                  sectionCompletion.leadInformation ? "completed" : ""
                }`}
              ></div>
              
              <div
                className={`progress-step ${
                  sectionCompletion.projectDetails ? "completed" : ""
                } ${activeSection === "project-details" ? "active-blink" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleProgressBarClick("project-details");
                }}
              >
                <h6>Department Progress Tracker</h6>
                <div className="progress-step-circle">
                  {sectionCompletion.projectDetails ? (
                    // Completed → checkmark
                    <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.5 4.5L6 12L2.5 8.5"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    // Not completed → cross icon (modern design)
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        fill="#dc3545"
                        opacity="0.1"
                      />
                      <path
                        d="M8 8L16 16M16 8L8 16"
                        stroke="#dc3545"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
                <div className="progress-step-label">Sales Department</div>
              </div>

              <div
                className={`progress-line ${
                  sectionCompletion.projectDetails ? "completed" : ""
                }`}
              ></div>

              <div
                className={`progress-step ${
                  sectionCompletion.addressInformation ? "completed" : ""
                } ${
                  activeSection === "address-information" ? "active-blink" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection("address-information");
                }}
              >
                <div className="progress-step-circle">
                  {sectionCompletion.addressInformation ? (
                    // Completed → checkmark
                    <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.5 4.5L6 12L2.5 8.5"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    // Not completed → cross icon (modern design)
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        fill="#dc3545"
                        opacity="0.1"
                      />
                      <path
                        d="M8 8L16 16M16 8L8 16"
                        stroke="#dc3545"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
                <div className="progress-step-label">Site Visit Department</div>
              </div>
            </div>
          </div> */}

            <BrowserView>
              <div className="section-progress-bar">
                <div className="progress-bar-container">
                  {/* Tele Department */}
                  <div
                    className={`progress-step ${sectionCompletion.leadInformation ? "completed" : ""
                      } ${activeSection === "lead-information" ? "active-blink" : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleProgressBarClick("lead-information");
                    }}
                  >
                    <div className="progress-step-circle">
                      {sectionCompletion.leadInformation
                        ? CHECK_ICON
                        : CROSS_ICON}
                    </div>
                    <div className="progress-step-label">Tele Department</div>
                  </div>

                  <div
                    className={`progress-line ${sectionCompletion.leadInformation ? "completed" : ""
                      }`}
                  />

                  {/* Sales Department */}
                  <div
                    className={`progress-step ${sectionCompletion.projectDetails ? "completed" : ""
                      } ${activeSection === "project-details" ? "active-blink" : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleProgressBarClick("project-details");
                    }}
                  >
                    <h6>Department Progress Tracker</h6>
                    <div className="progress-step-circle">
                      {sectionCompletion.projectDetails ? CHECK_ICON : CROSS_ICON}
                    </div>
                    <div className="progress-step-label">Sales Department</div>
                  </div>

                  <div
                    className={`progress-line ${sectionCompletion.projectDetails ? "completed" : ""
                      }`}
                  />

                  {/* Site Visit */}
                  <div
                    className={`progress-step ${sectionCompletion.addressInformation ? "completed" : ""
                      } ${activeSection === "address-information"
                        ? "active-blink"
                        : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection("address-information");
                    }}
                  >
                    <div className="progress-step-circle">
                      {sectionCompletion.addressInformation
                        ? CHECK_ICON
                        : CROSS_ICON}
                    </div>
                    <div className="progress-step-label">
                      Site Visit Department
                    </div>
                  </div>
                </div>
              </div>
            </BrowserView>

            <MobileView>
              <div className="section-progress-bar mobile-progress">
                <h6 className="mobile-step-title">Department Progress Tracker</h6>
                <div className="progress-bar-container mobile-progress-container">
                  <div
                    className={`progress-step ${sectionCompletion.leadInformation ? "completed" : ""
                      } ${activeSection === "lead-information" ? "active-blink" : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleProgressBarClick("lead-information");
                    }}
                  >
                    <div className="progress-step-circle">
                      {sectionCompletion.leadInformation
                        ? CHECK_ICON
                        : CROSS_ICON}
                    </div>
                    <div className="progress-step-label">Tele</div>
                  </div>

                  <div
                    className={`progress-step ${sectionCompletion.projectDetails ? "completed" : ""
                      } ${activeSection === "project-details" ? "active-blink" : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleProgressBarClick("project-details");
                    }}
                  >
                    <div className="progress-step-circle">
                      {sectionCompletion.projectDetails ? CHECK_ICON : CROSS_ICON}
                    </div>
                    <div className="progress-step-label">Sales</div>
                  </div>

                  <div
                    className={`progress-step ${sectionCompletion.addressInformation ? "completed" : ""
                      } ${activeSection === "address-information"
                        ? "active-blink"
                        : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection("address-information");
                    }}
                  >
                    <div className="progress-step-circle">
                      {sectionCompletion.addressInformation
                        ? CHECK_ICON
                        : CROSS_ICON}
                    </div>
                    <div className="progress-step-label">Site Visit</div>
                  </div>
                </div>
              </div>
            </MobileView>

            {/* Form Header - Title only; Save/Cancel moved to footer */}
            <CardHeader className="bg-custom text-white sticky-header-Lead p-3" >
              <Row className="w-100" >
                <Col xs={12} sm={12} md={6} lg={6} className="">
                  <div className="d-flex align-items-center h-100 gap-3">
                    {lead.id ? (
                      <h5 className="m-0">Edit Lead {lead.lead_name}</h5>
                    ) : (
                      <h5 className="m-0">Create Lead/Enquiry</h5>
                    )}
                  </div>
                </Col>
                <Col xs={12} sm={12} md={6} lg={6} className="">
                  <Stack
                    direction="horizontal"
                    className="float-end float-md-end"
                    gap={2}
                  >
                    <Button
                      size="sm"
                      className="btn-save"
                      type="submit"
                      onClick={handleSubmit}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      className="btn-cancel"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Col>
              </Row>
            </CardHeader>
          </Col>

          <Col md={12} className="">
            <Form
              id="lead-edit-form"
              className=""
              onSubmit={handleSubmit}
              noValidate
              validated={validated}
            >
              <Card className="shadow">
                <CardBody className="">
                  <Row lg={12} className="view-form ">
                    {
                      // Conditional rendering for Tele Caller Information section
                      visibleSections.teleCallerInfo && (
                        <>
                          <Col
                            md={12}
                            className="section-header"
                            id="lead-information"
                          >
                            Client Information{" "}
                            {!isSuperAdmin &&
                              userInfo?.user_type === "Sales Person" &&
                              "(View Only)"}
                          </Col>
                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicDate"
                              >
                                lead Date
                              </Form.Label>
                              <Form.Control
                                disabled
                                value={
                                  lead?.id
                                    ? moment(lead?.createddate).format(
                                      "DD-MM-YYYY"
                                    )
                                    : moment().format("DD-MM-YYYY")
                                }
                              />
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicLastName"
                              >
                                Lead Name
                              </Form.Label>
                              <Form.Control
                                required={
                                  isSuperAdmin ||
                                  userInfo?.user_type === "Tele-Caller"
                                }
                                disabled={isDisabled}
                                pattern={regExPattern.name}
                                type="text"
                                name="lead_name"
                                placeholder="Enter lead name"
                                value={lead?.lead_name || ""}
                                isInvalid={!!errors?.lead_name}
                                onChange={(e) =>
                                  handleUppercaseChange(e, handleChange)
                                }
                                onBlur={() => {
                                  if (!(isSuperAdmin || userInfo?.user_type === "Tele-Caller")) return;
                                  if (!lead?.lead_name || String(lead.lead_name).trim() === "") {
                                    setErrors((prev) => ({ ...prev, lead_name: { message: validationMessages.leadName.required } }));
                                  } else if (regExPattern?.name && !RegExp(regExPattern.name).test(lead.lead_name)) {
                                    setErrors((prev) => ({ ...prev, lead_name: { message: validationMessages.leadName.invalid } }));
                                  } else if (errors?.lead_name) {
                                    setErrors((prev) => { const next = { ...prev }; delete next.lead_name; return next; });
                                  }
                                }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors?.lead_name?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicEmail"
                              >
                                Email
                              </Form.Label>
                              <Form.Control
                                disabled={isDisabled}
                                isInvalid={!!errors?.email}
                                pattern={regExPattern.email}
                                type="email"
                                name="email"
                                placeholder="Enter email"
                                value={lead?.email || ""}
                                onChange={handleChange}
                              />

                              <Form.Control.Feedback type="invalid">
                                {errors?.email?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          {/* <Col sm={12} md={6} lg={6}>
                          <Form.Group>
                            <Form.Label
                              className="form-view-label"
                              htmlFor="formBasicState"
                            >
                              Referred By
                            </Form.Label>

                            <Select
                              name="contact_id"
                              // className={`${errors?.contact_id?.message ? 'border border-danger rounded' : 'border border-success rounded' }`}
                              placeholder="--- Select ---"
                              options={refContact?.map((contact) => ({
                                value: contact?.id,
                                // label: `${contact?.contact_name}   ${contact?.title}`,
                                label: `${contact?.contact_name} - ${contact?.title}`,
                              }))}
                              onChange={(e) => {
                                handleChange({
                                  target: {
                                    name: "contact_id",
                                    value: e.value,
                                  },
                                });
                                handleChange({
                                  target: {
                                    name: "contact_name",
                                    value: e.label,
                                  },
                                });
                              }}
                              value={
                                lead?.contact_id
                                  ? {
                                      value: lead?.contact_id,
                                      label: lead?.contact_name,
                                    }
                                  : ""
                              }
                            ></Select>
                            <Form.Control.Feedback type="invalid">
                              {errors?.contact_id?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col> */}

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicWhatsAppNumber"
                              >
                                Phone
                              </Form.Label>
                              <Form.Control
                                required={
                                  isSuperAdmin ||
                                  userInfo?.user_type === "Tele-Caller"
                                }
                                disabled={isDisabled}
                                type="text"
                                name="phone"
                                placeholder="Enter phone number"
                                value={lead?.phone || ""}
                                isInvalid={!!errors?.phone}
                                onChange={handleChange}
                                onBlur={() => {
                                  if (!(isSuperAdmin || userInfo?.user_type === "Tele-Caller")) return;
                                  if (!lead?.phone || String(lead.phone).trim() === "") {
                                    setErrors((prev) => ({ ...prev, phone: { message: validationMessages.phone.required } }));
                                  } else if (regExPattern?.phone && !RegExp(regExPattern.phone).test(lead.phone)) {
                                    setErrors((prev) => ({ ...prev, phone: { message: validationMessages.phone.invalid } }));
                                  } else if (errors?.phone) {
                                    setErrors((prev) => { const next = { ...prev }; delete next.phone; return next; });
                                  }
                                }}
                                maxLength={10}
                                pattern={regExPattern.phone}
                              />

                              <Form.Control.Feedback type="invalid">
                                {errors?.phone?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group style={{ position: "relative" }}>
                              <Form.Label className="form-view-label">
                                WhatsApp Number.
                              </Form.Label>

                              <Form.Control
                                type="text"
                                disabled={isDisabled}
                                name="whatsapp_no"
                                placeholder="Enter whatsApp number"
                                value={lead?.whatsapp_no || ""}
                                onChange={handleChange}
                                maxLength={10}
                                pattern={regExPattern.phone}
                              />

                              {/* <FaCopy
                              size={22}
                              style={{
                                position: "absolute",
                                right: "6px",
                                top: "40px",
                                cursor: lead?.whatsapp_no
                                  ? "pointer"
                                  : "not-allowed",
                                opacity: lead?.whatsapp_no ? 1 : 0.5,
                                zIndex: 0,
                              }}
                              onClick={() => {
                                const value = lead?.whatsapp_no || "";

                                if (!value) return; // empty → do nothing

                                if (navigator?.clipboard?.writeText) {
                                  navigator.clipboard.writeText(value);
                                } else {
                                  // fallback
                                  const temp = document.createElement("input");
                                  temp.value = value;
                                  document.body.appendChild(temp);
                                  temp.select();
                                  document.execCommand("copy");
                                  document.body.removeChild(temp);
                                }
                              }}
                            /> */}

                              <Form.Control.Feedback type="invalid">
                                {errors?.whatsapp_no?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicLeadSource"
                              >
                                lead Source
                              </Form.Label>
                              <Form.Select
                                disabled={isDisabled}
                                placeholder="Select lead source"
                                aria-label="Select lead source"
                                name="leadsource"
                                value={lead?.leadsource || ""}
                                onChange={handleChange}
                              >
                                <option value="">---Select---</option>
                                {/* Modified By Farhan Khan on 16-Feb-2026 (Include lead.leadsource in options if not in list - ensures edit pre-fill works for DB values) */}
                                {(() => {
                                  const opts = [...(leadSourceOptions || [])];
                                  const currentVal = lead?.leadsource ? String(lead.leadsource).trim() : "";
                                  if (currentVal && !opts.includes(currentVal)) opts.unshift(currentVal);
                                  return opts.map((item, index) => (
                                    <option value={item} key={`ls-${item}-${index}`}>
                                      {item}
                                    </option>
                                  ));
                                })()}
                              </Form.Select>

                              <Form.Control.Feedback type="invalid">
                                {errors?.leadsource?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          {lead?.leadsource === "Commission Agent" && (
                            <Col xs={12} sm={12} md={6} lg={6}>
                              <Form.Group>
                                <Form.Label
                                  className="form-view-label"
                                  htmlFor="formBasicLeadSource"
                                >
                                  Commission Applicable
                                </Form.Label>
                                <Form.Select
                                  required={
                                    lead?.leadsource === "Commission Agent"
                                      ? true
                                      : false
                                  }
                                  disabled={isDisabled}
                                  name="applied"
                                  value={lead?.applied || ""}
                                  onChange={handleChange}
                                  isInvalid={!!errors?.applied}
                                >
                                  {/* <option value="">---Select---</option>
                              {constants.sourceNames?.map((item, index) => (
                                <option value={item} key={index}>
                                  {item}
                                </option> */}
                                  <option value="">---Select---</option>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                  {/* ))} */}
                                </Form.Select>

                                <Form.Control.Feedback type="invalid">
                                  {errors?.applied?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          )}
                          {lead?.applied === "Yes" && (
                            <Col xs={12} sm={12} md={6} lg={6}>
                              <Form.Group>
                                <Form.Label
                                  className="form-view-label"
                                  htmlFor="formBasicPhone"
                                >
                                  Commission Amount
                                </Form.Label>

                                <Form.Control
                                  required
                                  type="number"
                                  name="amount"
                                  placeholder="Enter Commisssion Amount"
                                  value={accountAmount || ""}
                                  onChange={handleChange}
                                  isInvalid={!!errors?.amount}
                                  min={0}
                                  pattern={regExPattern.amount}
                                  onKeyDownCapture={(e) => {
                                    (e.key === " " ||
                                      (e.key.length === 1 && isNaN(e.key))) &&
                                      e.preventDefault();
                                  }}
                                />

                                <Form.Control.Feedback type="invalid">
                                  {errors?.amount?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          )}

                          <Col sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicSupplier"
                              >
                                Referred By
                              </Form.Label>
                              <BrowserView>
                                <InputGroup className="d-flex align-items-stretch">
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <Select
                                      className={`w-100 ${errors?.contact_id
                                        ? "custom-select-invalid"
                                        : lead?.contact_id
                                          ? "custom-select-valid"
                                          : ""
                                        }`}
                                      placeholder="...Select..."
                                      options={(() => {
                                        // Modified By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering)
                                        const formatLabel = (firstname, contactName, title) =>
                                          `${(firstname || contactName || "").trim()} - (${title || "N/A"})`;
                                        const contactOptions =
                                          refContact?.map((contact) => ({
                                            value: contact?.id,
                                            label: formatLabel(contact?.firstname, contact?.contact_name, contact?.title),
                                          })) || [];

                                        if (lead?.contact_id) {
                                          const idStr = String(lead.contact_id);
                                          const contactExists = contactOptions.some(
                                            (opt) => String(opt.value) === idStr
                                          );
                                          if (!contactExists) {
                                            const displayName = (lead?.contact_name || "").split(" - ")[0]?.trim() || lead?.contact_name || `Contact ${lead.contact_id}`;
                                            contactOptions.unshift({
                                              value: lead.contact_id,
                                              label: `${displayName} - (N/A)`,
                                            });
                                          }
                                        }
                                        return contactOptions;
                                        // End Here Comment By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering)
                                      })()}
                                      onChange={(e) => {
                                        // closeComparePrice();
                                        // onVendorInfoSelect(e);
                                        handleChange({
                                          target: {
                                            name: "contact_id",
                                            value: e.value,
                                          },
                                        });
                                        handleChange({
                                          target: {
                                            name: "contact_name",
                                            value: e.label,
                                          },
                                        });
                                      }}
                                      value={
                                        lead?.contact_id
                                          ? (() => {
                                            // Modified By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering)
                                            const idStr = String(lead.contact_id);
                                            const formatLabel = (firstname, contactName, title) =>
                                              `${(firstname || contactName || "").trim()} - (${title || "N/A"})`;
                                            const foundContact = refContact?.find(
                                              (c) => String(c?.id) === idStr
                                            );
                                            if (foundContact) {
                                              return {
                                                value: foundContact.id,
                                                label: formatLabel(foundContact.firstname, foundContact.contact_name, foundContact.title),
                                              };
                                            }
                                            const displayName = (lead?.contact_name || "").split(" - ")[0]?.trim() || lead?.contact_name || `Contact ${lead.contact_id}`;
                                            return {
                                              value: lead.contact_id,
                                              label: `${displayName} - (N/A)`,
                                            };
                                            // End Here Comment By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering)
                                          })()
                                          : null
                                      }
                                    />
                                  </div>
                                  <InputGroup.Text
                                    className="d-flex justify-content-center align-items-center p-0 bg-white flex-shrink-0"
                                    style={{ width: "2.75rem", cursor: "pointer" }}
                                  >
                                    <i
                                      className="fa fa-plus fa-xl text-success"
                                      onClick={handleContactModel}
                                    ></i>
                                  </InputGroup.Text>
                                </InputGroup>
                              </BrowserView>
                              <MobileView>
                                <InputGroup className="d-flex align-items-stretch">
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <Select
                                      className={`w-100 ${errors?.contact_id
                                        ? "custom-select-invalid"
                                        : lead?.contact_id
                                          ? "custom-select-valid"
                                          : ""
                                        }`}
                                      placeholder="...Select..."
                                      options={(() => {
                                        // Modified By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering)
                                        const formatLabel = (firstname, contactName, title) =>
                                          `${(firstname || contactName || "").trim()} - (${title || "N/A"})`;
                                        const contactOptions =
                                          refContact?.map((contact) => ({
                                            value: contact?.id,
                                            label: formatLabel(contact?.firstname, contact?.contact_name, contact?.title),
                                          })) || [];

                                        if (lead?.contact_id) {
                                          const idStr = String(lead.contact_id);
                                          const contactExists = contactOptions.some(
                                            (opt) => String(opt.value) === idStr
                                          );
                                          if (!contactExists) {
                                            const displayName = (lead?.contact_name || "").split(" - ")[0]?.trim() || lead?.contact_name || `Contact ${lead.contact_id}`;
                                            contactOptions.unshift({
                                              value: lead.contact_id,
                                              label: `${displayName} - (N/A)`,
                                            });
                                          }
                                        }
                                        return contactOptions;
                                        // End Here Comment By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering)
                                      })()}
                                      onChange={(e) => {
                                        // closeComparePrice();
                                        // onVendorInfoSelect(e);
                                        handleChange({
                                          target: {
                                            name: "contact_id",
                                            value: e.value,
                                          },
                                        });
                                        handleChange({
                                          target: {
                                            name: "contact_name",
                                            value: e.label,
                                          },
                                        });
                                      }}
                                      value={
                                        lead?.contact_id
                                          ? (() => {
                                            // Modified By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering)
                                            const idStr = String(lead.contact_id);
                                            const formatLabel = (firstname, contactName, title) =>
                                              `${(firstname || contactName || "").trim()} - (${title || "N/A"})`;
                                            const foundContact = refContact?.find(
                                              (c) => String(c?.id) === idStr
                                            );
                                            if (foundContact) {
                                              return {
                                                value: foundContact.id,
                                                label: formatLabel(foundContact.firstname, foundContact.contact_name, foundContact.title),
                                              };
                                            }
                                            const displayName = (lead?.contact_name || "").split(" - ")[0]?.trim() || lead?.contact_name || `Contact ${lead.contact_id}`;
                                            return {
                                              value: lead.contact_id,
                                              label: `${displayName} - (N/A)`,
                                            };
                                            // End Here Comment By Farhan Khan on 16-Feb-2026 (Fixed Reference By dropdown prefill without filtering)
                                          })()
                                          : null
                                      }
                                    />
                                  </div>
                                  <InputGroup.Text
                                    className="d-flex justify-content-center align-items-center p-0 bg-white flex-shrink-0"
                                    style={{ width: "2.75rem", cursor: "pointer" }}
                                  >
                                    <i
                                      className="fa fa-plus fa-xl text-success"
                                      onClick={handleContactModel}
                                    ></i>
                                  </InputGroup.Text>
                                </InputGroup>
                              </MobileView>

                              <Form.Control.Feedback type="invalid">
                                {errors?.contact_id?.message ||
                                  "Please select a referred bys"}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicWhatsAppNumber"
                              >
                                Background
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={isDisabled}
                                name="background"
                                placeholder="Enter background"
                                value={lead?.background || ""}
                                onChange={handleChange}
                                pattern={regExPattern.name}
                              />

                              <Form.Control.Feedback type="invalid">
                                {errors?.background?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicLeadDelegatedUsers"
                              >
                                Assign Users
                              </Form.Label>
                              <Select
                                disabled={isDisabled}
                                className={`${errors?.owner_ids || !Array.isArray(lead?.owner_ids) || lead?.owner_ids?.length === 0
                                  ? "custom-select-invalid"
                                  : "custom-select-valid"
                                  }`}
                                isMulti
                                placeholder="Select your Assign Staff"
                                options={assignUsers?.map((item) => ({
                                  value: item.id,
                                  label:
                                    item.userrole === "SUPER_ADMIN"
                                      ? item.username
                                      : item.userrole === "ADMIN"
                                        ? `${item.username} - (${item.user_type}) - (Manager)`
                                        : `${item.username} - (${item.user_type})`,

                                  // label: item.username,
                                  // label: `${item.userrole} === ADMIN ?  ${item.username} -  (${item.user_type}) - (Manager)`,
                                }))}
                                value={lead?.owner_ids?.filter(
                                  (item) => item.value
                                )}
                                onChange={(e) => {
                                  setActiveSection("lead-information");
                                  setTimeout(() => setActiveSection(null), 2000);
                                  handleChange({
                                    target: { name: "owner_ids", value: e },
                                  });
                                }}
                              />
                              {errors?.owner_ids?.message && validated && (
                                <div className="feedback-invalid">
                                  {errors?.owner_ids?.message}
                                </div>
                              )}
                            </Form.Group>
                          </Col>

                          {/* Customer Address Fields */}


                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicCustomerAddress"
                              >
                                Address
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={isDisabled}
                                name="customer_address"
                                placeholder="Enter customer address"
                                value={lead?.customer_address || ""}
                                onChange={(e) =>
                                  handleUppercaseChange(e, handleChange)
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors?.customer_address?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicCustomerArea"
                              >
                                Area
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={isDisabled}
                                name="customer_area"
                                placeholder="Enter customer area"
                                value={lead?.customer_area || ""}
                                onChange={(e) =>
                                  handleUppercaseChange(e, handleChange)
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors?.customer_area?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicCustomerState"
                              >
                                State
                              </Form.Label>
                              <Select
                                placeholder="Select customer state"
                                disabled={isDisabled}
                                options={states?.map((state) => ({
                                  value: state,
                                  label: state,
                                }))}
                                onChange={(e) => {
                                  setActiveSection("lead-information");
                                  setTimeout(() => setActiveSection(null), 2000);
                                  setLead((prev) => ({ ...prev, customer_city: "" }));
                                  handleChange({
                                    target: { name: "customer_state", value: e.value },
                                  });
                                }}
                                value={
                                  lead?.customer_state
                                    ? {
                                      value: lead?.customer_state || "",
                                      label: lead?.customer_state || "",
                                    }
                                    : ""
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors?.customer_state?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicCustomerCity"
                              >
                                City
                              </Form.Label>
                              <Select
                                placeholder="Select customer city"
                                disabled={isDisabled}
                                className={`${lead?.customer_city
                                  ? "custom-react-select-valid"
                                  : "custom-react-select-invalid"
                                  }`}
                                options={customerCities?.map((city) => ({
                                  value: city,
                                  label: city,
                                })) || []}
                                onChange={(e) => {
                                  handleChange({
                                    target: { name: "customer_city", value: e.value },
                                  });
                                }}
                                value={
                                  lead?.customer_city
                                    ? {
                                      value: lead?.customer_city || "",
                                      label: lead?.customer_city || "",
                                    }
                                    : ""
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors?.customer_city?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={12} md={6} lg={6}>
                            <Form.Group>
                              <Form.Label
                                className="form-view-label"
                                htmlFor="formBasicCustomerPincode"
                              >
                                Pincode
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled={isDisabled}
                                name="customer_pincode"
                                placeholder="Enter customer pincode"
                                value={lead?.customer_pincode || ""}
                                onChange={handleChange}
                                pattern={regExPattern.pinCode}
                                maxLength={6}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors?.customer_pincode?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </>
                      )
                    }
                    {/* <Col xs={12} sm={12} md={6} lg={6}>
                    <Form.Group>
                      <Form.Label>Lead Status</Form.Label>
                      <Form.Select
                        required={
                          isSuperAdmin ||
                          userInfo?.user_type === "Sales Person"
                        }
                        disabled={isDisabledSalesPeron}
                        name="lead_status"
                        value={lead?.lead_status || ""}
                        onChange={handleChange}
                      >
                        <option value="">--Select-Status--</option>
                        {statusArray.map((item, index) => (
                          <option value={item.label} key={index}>
                            {item.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors?.lead_status?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col> */}

                    {lead?.lead_status === "Hold" && (
                      <Col xs={12} sm={12} md={6} lg={6}>
                        <Form.Group>
                          <Form.Label
                            className="form-view-label"
                            htmlFor="formBasicLead"
                          >
                            Hold Reason
                          </Form.Label>
                          <Form.Select
                            disabled={isDisabledSalesPeron}
                            placeholder="Select hold reason"
                            name="hold_reason"
                            value={lead?.hold_reason || ""}
                            onChange={handleChange}
                          >
                            <option value="">---Select---</option>
                            {["Partial Work", "Delay", "Lost"].map(
                              (item, index) => (
                                <option value={item} key={index}>
                                  {item}
                                </option>
                              )
                            )}
                          </Form.Select>

                          <Form.Control.Feedback type="invalid">
                            {errors?.hold_reason?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    )}
                    {lead?.hold_reason === "Delay" && (
                      <Col xs={12} sm={12} md={6} lg={6}>
                        <Form.Group>
                          <Form.Label
                            className="form-view-label"
                            htmlFor="formBasicLead"
                          >
                            Delay Reason
                          </Form.Label>
                          <Form.Select
                            disabled={isDisabledSalesPeron}
                            placeholder="Select delay reason"
                            name="delay_reason"
                            value={lead?.delay_reason || ""}
                            onChange={handleChange}
                          >
                            <option value="">---Select---</option>
                            {[
                              "Load extension",
                              "Under construction",
                              "New connection",
                              "Name change",
                              "Other",
                              "Remark",
                            ].map((item, index) => (
                              <option value={item} key={index}>
                                {item}
                              </option>
                            ))}
                          </Form.Select>

                          <Form.Control.Feedback type="invalid">
                            {errors?.delay_reason?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    )}

                    {
                      // visibleSections.addressInfo && (
                      // Address Information section - always visible
                      (visibleSections.addressInfo || visibleSections.projectDetails) && (
                        (visibleSections.addressInfo || visibleSections.projectDetails) && (
                          <>
                            {/* Project Information & Address - Add Row */}
                            <div className="project-section mt-2">
                              <Row className="mt-2">
                                <Col lg={8}>
                                  <h3>Project Information & Address</h3>
                                  <p>Add multiple project/Address rows</p>
                                </Col>
                                <Col lg={4}>
                                  <Button
                                    size="md"
                                    className="btn-add-row float-end"
                                    // variant="light"
                                    onClick={() => {
                                      const lastIndex = projectLineItems.length - 1;
                                      if (projectLineItems.length > 0) {
                                        const rowErrors = validateProjectRowForAddRow(projectLineItems[lastIndex]);
                                        if (Object.keys(rowErrors).length > 0) {
                                          setProjectRowValidationErrors((prev) => ({ ...prev, [lastIndex]: rowErrors }));
                                          const firstErrField = Object.keys(rowErrors)[0];
                                          const fieldLabel = PROJECT_ROW_FIELD_LABELS[firstErrField] || (firstErrField && firstErrField.replace(/_/g, " "));
                                          const firstErrMsg = rowErrors[firstErrField];
                                          const isRequiredStyle = firstErrMsg && (String(firstErrMsg).toLowerCase().includes("provide") || String(firstErrMsg).toLowerCase().includes("required"));
                                          PubSub.publish("RECORD_ERROR_TOAST", {
                                            message: isRequiredStyle && fieldLabel ? `${fieldLabel} is Required` : (firstErrMsg || "Please fill all required fields in the current row before adding a new row."),
                                          });
                                          return;
                                        }
                                      }
                                      setProjectRowValidationErrors((prev) => {
                                        const next = { ...prev };
                                        delete next[projectLineItems.length - 1];
                                        return next;
                                      });
                                      setProjectLineItems((prev) => [...prev, getEmptyProjectRow()]);
                                    }}
                                  >
                                    Add Project
                                  </Button>
                                </Col>

                              </Row>




                              {projectLineItems.map((row, rowIndex) => (
                                <div key={rowIndex} className="project-row border p-3 mb-3 mt-4">
                                  <Row>
                                    <Col lg={12} className="d-flex justify-content-between align-items-center mb-3">
                                      <h5>Project: {rowIndex + 1}</h5>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => {
                                          setProjectLineItems((prev) => prev.filter((_, i) => i !== rowIndex));
                                          setProjectRowValidationErrors((prev) => {
                                            const next = {};
                                            Object.keys(prev).forEach((k) => {
                                              const ki = parseInt(k, 10);
                                              if (ki < rowIndex) next[ki] = prev[k];
                                              else if (ki > rowIndex) next[ki - 1] = prev[k];
                                            });
                                            return next;
                                          });
                                        }}
                                        title="Delete line item"
                                      >
                                        <FaTrash /> Remove
                                      </button>
                                    </Col>

                                    {/* PROJECT DETAILS SECTION */}
                                    <Col lg={12} className="section-header">
                                      PROJECT DETAILS
                                    </Col>

                                    {/* Site Name - Modified By Farhan Khan on 16-Feb-2026 (Site Name is required - add validation feedback) */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Site Name</Form.Label>
                                        <Form.Control
                                          required
                                          type="text"
                                          placeholder="Enter site name"
                                          value={row.site_name}
                                          isInvalid={!!projectRowValidationErrors[rowIndex]?.site_name}
                                          onChange={(e) => {
                                            const v = e.target.value.toUpperCase();
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              if (next[rowIndex]) {
                                                const rowErrs = { ...next[rowIndex] };
                                                delete rowErrs.site_name;
                                                next[rowIndex] = Object.keys(rowErrs).length ? rowErrs : undefined;
                                                if (!next[rowIndex]) delete next[rowIndex];
                                              }
                                              return next;
                                            });
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, site_name: v } : r))
                                            );
                                          }}
                                          onBlur={() => {
                                            const r = projectLineItems[rowIndex];
                                            const errs = isRowFilled(r) ? validateProjectRowForAddRow(r) : {};
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              if (Object.keys(errs).length > 0) next[rowIndex] = errs;
                                              else delete next[rowIndex];
                                              return next;
                                            });
                                          }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          {projectRowValidationErrors[rowIndex]?.site_name}
                                        </Form.Control.Feedback>
                                      </Form.Group>
                                    </Col>
                                    {/* End Here Comment By Farhan Khan on 16-Feb-2026 */}

                                    {/* Lead Type */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Lead Type</Form.Label>
                                        <Form.Control
                                          required
                                          as="select"
                                          value={row.leadtype}
                                          isInvalid={!!projectRowValidationErrors[rowIndex]?.leadtype}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            console.log(`[Lead Field Change] projectRow[${rowIndex}].leadtype:`, `'${v}'`, "(from form input)");
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              delete next[rowIndex];
                                              return next;
                                            });
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, leadtype: v } : r))
                                            );
                                          }}
                                          onBlur={() => {
                                            const r = projectLineItems[rowIndex];
                                            const errs = isRowFilled(r) ? validateProjectRowForAddRow(r) : {};
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              if (Object.keys(errs).length > 0) next[rowIndex] = errs;
                                              else delete next[rowIndex];
                                              return next;
                                            });
                                          }}
                                        >
                                          <option value="">---Select---</option>
                                          {["New", "Modification", "AMC", "OneTime"].map((opt) => (
                                            <option key={opt} value={opt}>
                                              {opt}
                                            </option>
                                          ))}
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                          {projectRowValidationErrors[rowIndex]?.leadtype}
                                        </Form.Control.Feedback>
                                      </Form.Group>
                                    </Col>

                                    {/* Project Status (per project, independent of lead) */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Project Status</Form.Label>
                                        <Form.Control
                                          as="select"
                                          value={row.project_status || ""}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, project_status: v } : r))
                                            );
                                          }}
                                        >
                                          <option value="">--Select-Status--</option>
                                          {statusProjectArray.map((item, index) => (
                                            <option key={index} value={item.label}>
                                              {item.label}
                                            </option>
                                          ))}
                                        </Form.Control>
                                      </Form.Group>
                                    </Col>

                                    {/* Hold Reason - Conditional */}
                                    {row.project_status === "Hold" && (
                                      <Col xs={12} sm={12} md={6} lg={6}>
                                        <Form.Group>
                                          <Form.Label>Hold Reason</Form.Label>
                                          <Form.Control
                                            as="select"
                                            value={row.hold_reason}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setProjectLineItems((prev) =>
                                                prev.map((r, i) => (i === rowIndex ? { ...r, hold_reason: v } : r))
                                              );
                                            }}
                                          >
                                            <option value="">---Select---</option>
                                            {["Partial Work", "Delay", "Lost"].map((item, index) => (
                                              <option key={index} value={item}>
                                                {item}
                                              </option>
                                            ))}
                                          </Form.Control>
                                        </Form.Group>
                                      </Col>
                                    )}

                                    {/* Delay Reason - Conditional */}
                                    {row.hold_reason === "Delay" && (
                                      <Col xs={12} sm={12} md={6} lg={6}>
                                        <Form.Group>
                                          <Form.Label>Delay Reason</Form.Label>
                                          <Form.Control
                                            as="select"
                                            value={row.delay_reason}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setProjectLineItems((prev) =>
                                                prev.map((r, i) => (i === rowIndex ? { ...r, delay_reason: v } : r))
                                              );
                                            }}
                                          >
                                            <option value="">---Select---</option>
                                            {[
                                              "Load extension",
                                              "Under construction",
                                              "New connection",
                                              "Name change",
                                              "Other",
                                              "Remark",
                                            ].map((item, index) => (
                                              <option key={index} value={item}>
                                                {item}
                                              </option>
                                            ))}
                                          </Form.Control>
                                        </Form.Group>
                                      </Col>
                                    )}

                                    {/* Type of Project */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Type of Project</Form.Label>
                                        <Form.Control
                                          as="select"
                                          value={row.type_of_project}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, type_of_project: v } : r))
                                            );
                                          }}
                                        >
                                          <option value="">---Select---</option>
                                          <option value="Residential">Residential</option>
                                          <option value="Commercial">Commercial</option>
                                          <option value="Industrial">Industrial</option>
                                          <option value="Others">Others</option>
                                        </Form.Control>
                                      </Form.Group>
                                    </Col>

                                    {/* Site Contact Person No - 10 digits only, Same as Lead Phone button */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Site Contact Person No</Form.Label>
                                        <InputGroup>
                                          <Form.Control
                                            required
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="Enter 10 digit contact number"
                                            value={row.on_site_contact_person_no}
                                            maxLength={10}
                                            isInvalid={!!projectRowValidationErrors[rowIndex]?.on_site_contact_person_no}
                                            onChange={(e) => {
                                              const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                                              console.log(`[Lead Field Change] projectRow[${rowIndex}].on_site_contact_person_no:`, `'${v}'`, "(from form input)");
                                              setProjectRowValidationErrors((prev) => {
                                                const next = { ...prev };
                                                delete next[rowIndex];
                                                return next;
                                              });
                                              setProjectLineItems((prev) =>
                                                prev.map((r, i) =>
                                                  i === rowIndex ? { ...r, on_site_contact_person_no: v } : r
                                                )
                                              );
                                            }}
                                            onBlur={() => {
                                              const r = projectLineItems[rowIndex];
                                              const errs = isRowFilled(r) ? validateProjectRowForAddRow(r) : {};
                                              setProjectRowValidationErrors((prev) => {
                                                const next = { ...prev };
                                                if (Object.keys(errs).length > 0) next[rowIndex] = errs;
                                                else delete next[rowIndex];
                                                return next;
                                              });
                                            }}
                                          />
                                          <Button
                                            type="button"
                                            variant="outline-secondary"
                                            size="sm"
                                            className="px-2"
                                            onClick={() => {
                                              const leadPhone = lead?.phone;
                                              if (leadPhone && String(leadPhone).trim() !== "") {
                                                const digits = String(leadPhone).replace(/\D/g, "").slice(0, 10);
                                                console.log(`[Lead Field Change] projectRow[${rowIndex}].on_site_contact_person_no:`, `'${digits}'`, "(from Lead.phone)");
                                                setProjectRowValidationErrors((prev) => {
                                                  const next = { ...prev };
                                                  delete next[rowIndex];
                                                  return next;
                                                });
                                                setProjectLineItems((prev) =>
                                                  prev.map((r, i) =>
                                                    i === rowIndex ? { ...r, on_site_contact_person_no: digits } : r
                                                  )
                                                );
                                              }
                                            }}
                                            title="Same as Lead Phone"
                                          >
                                            Same as Lead Phone
                                          </Button>
                                        </InputGroup>
                                        <Form.Control.Feedback type="invalid">
                                          {projectRowValidationErrors[rowIndex]?.on_site_contact_person_no}
                                        </Form.Control.Feedback>
                                      </Form.Group>
                                    </Col>

                                    {/* Plant Capacity Required - single digit (0-9) only */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Plant Capacity Required (KW)</Form.Label>
                                        <Form.Control
                                          type="text"
                                          inputMode="numeric"
                                          placeholder="e.g. 1, 14, 100"
                                          value={row.plant_capacity_required_by_client}
                                          isInvalid={!!projectRowValidationErrors[rowIndex]?.plant_capacity_required_by_client}
                                          onChange={(e) => {
                                            const v = sanitizeNumericInput(e.target.value);
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              if (next[rowIndex]) {
                                                const rowErr = { ...next[rowIndex] };
                                                delete rowErr.plant_capacity_required_by_client;
                                                next[rowIndex] = Object.keys(rowErr).length ? rowErr : undefined;
                                                if (!next[rowIndex]) delete next[rowIndex];
                                              }
                                              return next;
                                            });
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) =>
                                                i === rowIndex ? { ...r, plant_capacity_required_by_client: v } : r
                                              )
                                            );
                                          }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          {projectRowValidationErrors[rowIndex]?.plant_capacity_required_by_client}
                                        </Form.Control.Feedback>
                                      </Form.Group>
                                    </Col>

                                    {/* Type of Roof */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Type of Roof</Form.Label>
                                        <Form.Control
                                          as="select"
                                          value={row.type_of_roof}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, type_of_roof: v } : r))
                                            );
                                          }}
                                        >
                                          <option value="">---Select---</option>
                                          <option value="RCC">RCC</option>
                                          <option value="Tin SHADE">TIN SHADE</option>
                                          <option value="GROUND">GROUND</option>
                                          <option value="COMBO">COMBO</option>
                                        </Form.Control>
                                      </Form.Group>
                                    </Col>

                                    {/* Type of Connection */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Type of Connection</Form.Label>
                                        <Form.Control
                                          as="select"
                                          value={row.type_of_connection}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, type_of_connection: v } : r))
                                            );
                                          }}
                                        >
                                          <option value="">---Select---</option>
                                          <option value="1PH">1PH</option>
                                          <option value="3PH">3PH</option>
                                          <option value="LTCT">LTCT</option>
                                          <option value="HTCT">HTCT</option>
                                          <option value="NO-CONNECTION">NO-CONNECTION</option>
                                        </Form.Control>
                                      </Form.Group>
                                    </Col>

                                    {/* Sanction Load - Project has ZERO validation */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Sanction Load (KW)</Form.Label>
                                        <Form.Control
                                          type="text"
                                          inputMode="numeric"
                                          placeholder="e.g. 1, 14, 100"
                                          value={row.sanction_load}
                                          onChange={(e) => {
                                            const v = sanitizeNumericInput(e.target.value);
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, sanction_load: v } : r))
                                            );
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>

                                    {/* Connection Modification */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Connection Modification</Form.Label>
                                        <Form.Control
                                          as="select"
                                          value={row.connection_modification}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) =>
                                                i === rowIndex
                                                  ? {
                                                    ...r,
                                                    connection_modification: v,
                                                    connection_modification_applicable:
                                                      v === "Non Required" ? "" : r.connection_modification_applicable,
                                                  }
                                                  : r
                                              )
                                            );
                                          }}
                                        >
                                          <option value="">---Select---</option>
                                          <option value="Required">Required</option>
                                          <option value="Non Required">Non Required</option>
                                        </Form.Control>
                                      </Form.Group>
                                    </Col>

                                    {/* Type of Modification - Conditional */}
                                    {row.connection_modification === "Required" && (
                                      <Col xs={12} sm={12} md={6} lg={6}>
                                        <Form.Group>
                                          <Form.Label>Type of Modification</Form.Label>
                                          <Form.Control
                                            as="select"
                                            value={row.connection_modification_applicable}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setProjectLineItems((prev) =>
                                                prev.map((r, i) =>
                                                  i === rowIndex ? { ...r, connection_modification_applicable: v } : r
                                                )
                                              );
                                            }}
                                          >
                                            <option value="">---Select---</option>
                                            <option value="Load extension">Load extension</option>
                                            <option value="New connection">New connection</option>
                                            <option value="Load reduction">Load reduction</option>
                                            <option value="Phase change">Phase change</option>
                                            <option value="Name correction">Name correction</option>
                                            <option value="Name transfer">Name transfer</option>
                                            <option value="Modified load">Modified load</option>
                                            <option value="No modification">No modification</option>
                                          </Form.Control>
                                        </Form.Group>
                                      </Col>
                                    )}

                                    {/* No of Floors */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>No of Floors</Form.Label>
                                        <Form.Control
                                          type="number"
                                          placeholder="Enter number of floors"
                                          value={row.no_of_floors}
                                          onChange={(e) => {
                                            const v = e.target.value.toUpperCase();
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, no_of_floors: v } : r))
                                            );
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>

                                    {/* Remark - Conditional */}
                                    {row.type_of_roof === "Combo" && (
                                      <Col xs={12} sm={12} md={6} lg={6}>
                                        <Form.Group>
                                          <Form.Label>Remark</Form.Label>
                                          <Form.Control
                                            type="text"
                                            placeholder="Enter remark"
                                            value={row.remark}
                                            onChange={(e) => {
                                              const v = e.target.value.toUpperCase();
                                              setProjectLineItems((prev) =>
                                                prev.map((r, i) => (i === rowIndex ? { ...r, remark: v } : r))
                                              );
                                            }}
                                          />
                                        </Form.Group>
                                      </Col>
                                    )}

                                    {/* Average Monthly Bill */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Average Monthly Bill</Form.Label>
                                        <Form.Control
                                          type="number"
                                          placeholder="Enter average monthly bill"
                                          value={row.average_monthly_bill}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, average_monthly_bill: v } : r))
                                            );
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>

                                    {/* Module Type (DCR) */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Module Type</Form.Label>
                                        <Form.Control
                                          as="select"
                                          value={row.dcr}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, dcr: v } : r))
                                            );
                                          }}
                                        >
                                          <option value="">---Select---</option>
                                          <option value="DCR">DCR</option>
                                          <option value="NON DCR">NON DCR</option>
                                          <option value="COMBO">COMBO</option>
                                        </Form.Control>
                                      </Form.Group>
                                    </Col>

                                    {/* Description - Conditional */}
                                    {row.type_of_connection && (
                                      <Col xs={12} sm={12} md={6} lg={6}>
                                        <Form.Group>
                                          <Form.Label>Description</Form.Label>
                                          <Form.Control
                                            as="textarea"
                                            rows={3}
                                            placeholder="Enter description"
                                            value={row.description}
                                            onChange={(e) => {
                                              const v = e.target.value.toUpperCase();
                                              setProjectLineItems((prev) =>
                                                prev.map((r, i) => (i === rowIndex ? { ...r, description: v } : r))
                                              );
                                            }}
                                          />
                                        </Form.Group>
                                      </Col>
                                    )}

                                    {/* ADDRESS INFORMATION SECTION */}
                                    <Col lg={12} className="section-header mt-3" id="address-information">
                                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <span>ADDRESS INFORMATION</span>
                                        <Button
                                          type="button"
                                          variant="success"
                                          // className="btn-create"
                                          // className="btn-save"
                                          size="sm"
                                          onClick={() => {
                                            const addr =
                                              lead?.customer_address || lead?.address || "";
                                            const stateVal =
                                              lead?.customer_state || lead?.state || "";
                                            const cityVal =
                                              lead?.customer_city || lead?.city || "";
                                            const pinVal =
                                              lead?.customer_pincode || lead?.pincode || "";
                                            const countryVal =
                                              lead?.country || "India";
                                            console.log(`[Lead Field Change] projectRow[${rowIndex}].address,state,city,pincode:`, { address: addr, state: stateVal, city: cityVal, pincode: pinVal }, "(from Lead address)");
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) =>
                                                i === rowIndex
                                                  ? {
                                                    ...r,
                                                    address: addr,
                                                    state: stateVal,
                                                    city: cityVal,
                                                    pincode: pinVal,
                                                    country: countryVal,
                                                  }
                                                  : r
                                              )
                                            );
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              delete next[rowIndex];
                                              return next;
                                            });
                                          }}
                                          title="Copy Lead address into this row"
                                        >
                                          Same as Lead Address
                                        </Button>
                                      </div>
                                    </Col>

                                    {/* Location Link */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label className="form-view-label">Location Link</Form.Label>
                                        <Form.Control
                                          type="text"
                                          placeholder="Enter location link"
                                          value={row.location_link}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, location_link: v } : r))
                                            );
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>

                                    {/* Address */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label>Address</Form.Label>
                                        <Form.Control
                                          required
                                          type="text"
                                          placeholder="Enter address"
                                          value={row.address}
                                          isInvalid={!!projectRowValidationErrors[rowIndex]?.address}
                                          onChange={(e) => {
                                            const v = e.target.value.toUpperCase();
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              delete next[rowIndex];
                                              return next;
                                            });
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, address: v } : r))
                                            );
                                          }}
                                          onBlur={() => {
                                            const r = projectLineItems[rowIndex];
                                            const errs = isRowFilled(r) ? validateProjectRowForAddRow(r) : {};
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              if (Object.keys(errs).length > 0) next[rowIndex] = errs;
                                              else delete next[rowIndex];
                                              return next;
                                            });
                                          }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          {projectRowValidationErrors[rowIndex]?.address}
                                        </Form.Control.Feedback>
                                      </Form.Group>
                                    </Col>

                                    {/* State */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label className="form-view-label">State</Form.Label>
                                        <Select
                                          required
                                          placeholder="Select your state"
                                          className={projectRowValidationErrors[rowIndex]?.state ? "custom-react-select-invalid" : ""}
                                          options={states?.map((state) => ({
                                            value: state,
                                            label: state,
                                          }))}
                                          onChange={(e) => {
                                            console.log(`[Lead Field Change] projectRow[${rowIndex}].state:`, `'${e?.value}'`, "(from form input)");
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              delete next[rowIndex];
                                              return next;
                                            });
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) =>
                                                i === rowIndex ? { ...r, state: e.value, city: "" } : r
                                              )
                                            );
                                          }}
                                          onBlur={() => {
                                            const r = projectLineItems[rowIndex];
                                            const errs = isRowFilled(r) ? validateProjectRowForAddRow(r) : {};
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              if (Object.keys(errs).length > 0) next[rowIndex] = errs;
                                              else delete next[rowIndex];
                                              return next;
                                            });
                                          }}
                                          value={
                                            row.state
                                              ? {
                                                value: row.state,
                                                label: row.state,
                                              }
                                              : null
                                          }
                                        />
                                        {projectRowValidationErrors[rowIndex]?.state && (
                                          <div className="feedback-invalid">
                                            {projectRowValidationErrors[rowIndex]?.state}
                                          </div>
                                        )}
                                      </Form.Group>
                                    </Col>

                                    {/* City */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label className="form-view-label">City</Form.Label>
                                        <Form.Control
                                          type="text"
                                          placeholder="Enter City"
                                          value={row.city}
                                          required
                                          isInvalid={!!projectRowValidationErrors[rowIndex]?.city}
                                          onChange={(e) => {
                                            const v = e.target.value.toUpperCase();
                                            console.log(`[Lead Field Change] projectRow[${rowIndex}].city:`, `'${v}'`, "(from form input)");
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              delete next[rowIndex];
                                              return next;
                                            });
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, city: v } : r))
                                            );
                                          }}
                                          onBlur={() => {
                                            const r = projectLineItems[rowIndex];
                                            const errs = isRowFilled(r) ? validateProjectRowForAddRow(r) : {};
                                            setProjectRowValidationErrors((prev) => {
                                              const next = { ...prev };
                                              if (Object.keys(errs).length > 0) next[rowIndex] = errs;
                                              else delete next[rowIndex];
                                              return next;
                                            });
                                          }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          {projectRowValidationErrors[rowIndex]?.city}
                                        </Form.Control.Feedback>
                                      </Form.Group>
                                    </Col>

                                    {/* Pincode - Project has ZERO validation */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label className="form-view-label">Pincode</Form.Label>
                                        <Form.Control
                                          type="text"
                                          placeholder="Enter pincode"
                                          value={row.pincode}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, pincode: v } : r))
                                            );
                                          }}
                                          maxLength={6}
                                        />
                                      </Form.Group>
                                    </Col>

                                    {/* Country */}
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                      <Form.Group>
                                        <Form.Label className="form-view-label">Country</Form.Label>
                                        <Form.Control
                                          type="text"
                                          placeholder="Enter country"
                                          value={row.country}
                                          onChange={(e) => {
                                            const v = e.target.value.toUpperCase();
                                            setProjectLineItems((prev) =>
                                              prev.map((r, i) => (i === rowIndex ? { ...r, country: v } : r))
                                            );
                                          }}
                                          pattern={regExPattern.name}
                                        />
                                      </Form.Group>
                                    </Col>

                                    {/* // Model Code for Ref Partner Added By Jaseem Hussain on 04-July-2025 */}
                                    <Modal
                                      show={showContactModel}
                                      aria-labelledby="contained-modal-title-vcenter"
                                      centered
                                    >
                                      <Modal.Header>
                                        <Modal.Title id="contained-modal-title-vcenter">
                                          Create
                                        </Modal.Title>
                                      </Modal.Header>
                                      <Modal.Body>
                                        <Container className="view-form">
                                          <Row>
                                            <Col lg={12}>
                                              <Form
                                                className="mt-3"
                                                noValidate
                                                validated={validated}
                                                onSubmit={handleSubmitContact}
                                                controlId="taskEdit"
                                              >
                                                <Row>
                                                  <Col lg={6}>
                                                    <Form.Group
                                                      className="mx-3"
                                                      controlId="formBasicTitle"
                                                    >
                                                      <Form.Label
                                                        className="form-view-label"
                                                        htmlFor="formBasicTitle"
                                                      >
                                                        Name
                                                      </Form.Label>
                                                      <Form.Control
                                                        required
                                                        type="text"
                                                        name="firstname"
                                                        placeholder="Enter Name"
                                                        onChange={
                                                          handleChangeContactPartner
                                                        }
                                                      />
                                                    </Form.Group>
                                                  </Col>

                                                  <Col lg={6}>
                                                    <Form.Group
                                                      className="mx-3"
                                                      controlId="formBasicTitle"
                                                    >
                                                      <Form.Label
                                                        className="form-view-label"
                                                        htmlFor="formBasicTitle"
                                                      >
                                                        Mobile Number
                                                      </Form.Label>
                                                      <Form.Control
                                                        style={{ height: "36px" }}
                                                        type="phone"
                                                        minLength="10"
                                                        maxLength="10"
                                                        name="phone"
                                                        placeholder="Enter mobile number"
                                                        onKeyDownCapture={(e) => {
                                                          (e?.key == " " ||
                                                            (e.key.length == 1 &&
                                                              isNaN(e.key))) &&
                                                            e.preventDefault();
                                                        }}
                                                        onChange={
                                                          handleChangeContactPartner
                                                        }
                                                      />
                                                    </Form.Group>
                                                  </Col>

                                                  {/* <Col>
                                        <Form.Group
                                          className="mx-3"
                                          controlId="formBasicTitle"
                                        >
                                          <Form.Label
                                            className="form-view-label"
                                            htmlFor="formBasicTitle"
                                          >
                                            Last Name
                                          </Form.Label>
                                          <Form.Control
                                            required
                                            type="text"
                                            name="lastname"
                                            placeholder="Enter Last Name"
                                            onChange={
                                              handleChangeContactPartner
                                            }
                                          />
                                        </Form.Group>
                                      </Col> */}
                                                </Row>
                                                <Row>
                                                  <Col xs={12} sm={12} md={6} lg={12}>
                                                    <Form.Group
                                                      className="mx-3"
                                                      controlId="formBasicTitle"
                                                    >
                                                      <Form.Label
                                                        className="form-view-label"
                                                        htmlFor="formBasicEmail"
                                                      >
                                                        Email
                                                      </Form.Label>
                                                      <Form.Control
                                                        pattern={regExPattern.email}
                                                        type="email"
                                                        name="email"
                                                        placeholder="Enter email"
                                                        isInvalid={!!errors?.email}
                                                        // value={contact?.email || ""}
                                                        onChange={
                                                          handleChangeContactPartner
                                                        }
                                                      />

                                                      <Form.Control.Feedback type="invalid">
                                                        {errors?.email?.message}
                                                      </Form.Control.Feedback>
                                                    </Form.Group>
                                                  </Col>
                                                  {/* <Col>
                                        <Form.Group
                                          className="mx-3"
                                          controlId="formBasicTitle"
                                        >
                                          <Form.Label
                                            className="form-view-label"
                                            htmlFor="formBasicTitle"
                                          >
                                            Mobile Number
                                          </Form.Label>
                                          <Form.Control
                                            style={{ height: "36px" }}
                                            required
                                            type="phone"
                                            minLength="10"
                                            maxLength="10"
                                            name="phone"
                                            placeholder="Enter mobile number"
                                            onKeyDownCapture={(e) => {
                                              (e?.key == " " ||
                                                (e.key.length == 1 &&
                                                  isNaN(e.key))) &&
                                                e.preventDefault();
                                            }}
                                            onChange={
                                              handleChangeContactPartner
                                            }
                                          />
                                        </Form.Group>
                                      </Col> */}
                                                </Row>
                                              </Form>
                                            </Col>
                                          </Row>
                                        </Container>
                                      </Modal.Body>
                                      <Modal.Footer>
                                        <Button
                                          variant="success"
                                          onClick={handleSubmitContact}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          onClick={handleContactModelClose}
                                          variant="light"
                                        >
                                          Close
                                        </Button>
                                      </Modal.Footer>
                                    </Modal>
                                    {/* //End Here By Jaseem Hussain on 04-July-2025 */}
                                  </Row>
                                </div>
                              ))}
                              {/* Added By Farhan Khan on 11-Feb-2026 : User Can Add Row on From Below LineItems */}
                              <Row>
                                <Col lg={12}>
                                  <Button
                                    size="md"
                                    className="btn-add-row float-end"
                                    // variant="light"
                                    onClick={() => {
                                      const lastIndex = projectLineItems.length - 1;
                                      if (projectLineItems.length > 0) {
                                        const rowErrors = validateProjectRowForAddRow(projectLineItems[lastIndex]);
                                        if (Object.keys(rowErrors).length > 0) {
                                          setProjectRowValidationErrors((prev) => ({ ...prev, [lastIndex]: rowErrors }));
                                          const firstErrField = Object.keys(rowErrors)[0];
                                          const fieldLabel = PROJECT_ROW_FIELD_LABELS[firstErrField] || (firstErrField && firstErrField.replace(/_/g, " "));
                                          const firstErrMsg = rowErrors[firstErrField];
                                          const isRequiredStyle = firstErrMsg && (String(firstErrMsg).toLowerCase().includes("provide") || String(firstErrMsg).toLowerCase().includes("required"));
                                          PubSub.publish("RECORD_ERROR_TOAST", {
                                            message: isRequiredStyle && fieldLabel ? `${fieldLabel} is Required` : (firstErrMsg || "Please fill all required fields in the current row before adding a new row."),
                                          });
                                          return;
                                        }
                                      }
                                      setProjectRowValidationErrors((prev) => {
                                        const next = { ...prev };
                                        delete next[projectLineItems.length - 1];
                                        return next;
                                      });
                                      setProjectLineItems((prev) => [...prev, getEmptyProjectRow()]);
                                    }}
                                  >
                                    Add Project
                                  </Button>
                                </Col>
                              </Row>
                              {/* End Here Farhan Khan on 11-Feb-2026 */}
                            </div>
                          </>
                        )
                      )
                    }
                    {/* // Model Code for Ref Partner Added By Jaseem Hussain on 04-July-2025 */}
                    <Modal
                      show={showContactModel}
                      aria-labelledby="contained-modal-title-vcenter"
                      centered
                    >
                      <Modal.Header>
                        <Modal.Title id="contained-modal-title-vcenter">
                          Create Referral Partner
                        </Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <Container className="view-form">
                          <Row>
                            <Col lg={12}>
                              <Form
                                className="mt-3"
                                noValidate
                                validated={validated}
                                onSubmit={handleSubmitContact}
                                controlId="taskEdit"
                              >
                                <Row>
                                  <Col lg={6}>
                                    <Form.Group
                                      className="mx-3"
                                      controlId="formBasicTitle"
                                    >
                                      <Form.Label
                                        className="form-view-label"
                                        htmlFor="formBasicTitle"
                                      >
                                        Name
                                      </Form.Label>
                                      <Form.Control
                                        required
                                        type="text"
                                        name="firstname"
                                        placeholder="Enter Name"
                                        onChange={
                                          handleChangeContactPartner
                                        }
                                      />
                                    </Form.Group>
                                  </Col>

                                  <Col lg={6}>
                                    <Form.Group
                                      className="mx-3"
                                      controlId="formBasicTitle"
                                    >
                                      <Form.Label
                                        className="form-view-label"
                                        htmlFor="formBasicTitle"
                                      >
                                        Mobile Number
                                      </Form.Label>
                                      <Form.Control
                                        required
                                        style={{ height: "36px" }}
                                        type="phone"
                                        minLength="10"
                                        maxLength="10"
                                        name="phone"
                                        placeholder="Enter mobile number"
                                        onKeyDownCapture={(e) => {
                                          (e?.key == " " ||
                                            (e.key.length == 1 &&
                                              isNaN(e.key))) &&
                                            e.preventDefault();
                                        }}
                                        onChange={
                                          handleChangeContactPartner
                                        }
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col lg={6} >
                                    <Form.Group className="mx-3">
                                      <Form.Label
                                        controlId="formBasicTitle"
                                        className="form-view-label"
                                        htmlFor="formBasicTitle"
                                      >
                                        Contact Type
                                      </Form.Label>
                                      <Form.Select
                                        required
                                        placeholder="Select contact type"
                                        aria-label="Select contact type"
                                        name="title"
                                        // value={contact?.title || ""}
                                        onChange={handleChangeContactPartner}
                                      >
                                        <option value="">---Select---</option>
                                        {titles.map((title, index) => (
                                          <option value={title.value} key={index}>
                                            {title.label}
                                          </option>
                                        ))}
                                      </Form.Select>

                                      <Form.Control.Feedback type="invalid">
                                        {errors?.title?.message}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </Col>

                                  <Col lg={6}>
                                    <Form.Group
                                      className="mx-3"
                                      controlId="formBasicTitle"
                                    >
                                      <Form.Label
                                        className="form-view-label"
                                        htmlFor="formBasicEmail"
                                      >
                                        Email
                                      </Form.Label>
                                      <Form.Control
                                        pattern={regExPattern.email}
                                        type="email"
                                        name="email"
                                        placeholder="Enter email"
                                        isInvalid={!!errors?.email}
                                        // value={contact?.email || ""}
                                        onChange={
                                          handleChangeContactPartner
                                        }
                                      />

                                      <Form.Control.Feedback type="invalid">
                                        {errors?.email?.message}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </Col>
                                </Row>
                                {/* <Row> */}
                              </Form>
                            </Col>
                          </Row>
                        </Container>
                      </Modal.Body>
                      <Modal.Footer>
                        <Button
                          variant="success"
                          onClick={handleSubmitContact}
                        >
                          Save
                        </Button>
                        <Button
                          onClick={handleContactModelClose}
                          variant="light"
                        >
                          Close
                        </Button>
                      </Modal.Footer>
                    </Modal>
                    {/* //End Here By Jaseem Hussain on 04-July-2025 */}
                  </Row>
                </CardBody>
              </Card>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LeadEdit;
